import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Portal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  Checkbox,
  Switch
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useLocation, useNavigate } from "react-router-dom";
import SidebarLayout from "../components/sidebarLayout";
import { getAllTavoli, updateCompatibleSetups } from "../APIs/TablesAPI";
import { getAllSetups } from "../APIs/SetupsAPI";
import { creaCliente, createCompany, getAllClienti, getAllCompanies } from "../APIs/ClientsAPI";
import { getAllPackages } from "../APIs/PackagesAPI";
import { getAllProdotti, getProductServiceAvailability } from "../APIs/ProductsAPI";
import { getAllUsageTypes } from "../APIs/UsageTypesAPI";
import { getAllDepartments, getMyDepartmentScope } from "../APIs/DepartmentsAPI";
import { inviaPreventivoEmail } from "../APIs/PreventiviAPI";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import Autocomplete from '../components/SearchableAutocomplete';
import TextField from '../components/SearchableTextField';
import TimePicker24Field from "../components/TimePicker24Field";
import {
  createReservationEvent,
  createReservationEventPrepayment,
  deleteReservationEvent,
  downloadReservationEventProformaPdf,
  downloadReservationEventSummaryPdf,
  getReservationEventPrepaymentSummary,
  getReservationEvents,
  markReservationEventQuoteSent,
  refundReservationEventPrepayment,
  reverseReservationEventPrepayment,
  sendReservationEventSummaryEmail,
  patchReservationEventStatus,
  updateReservationEvent,
  uploadReservationEventAttachment,
} from "../APIs/ReservationEventsAPI";
import { useSnackbar } from "../contexts/SnackBarContext";
import { getTenantSettings } from "../APIs/TenantSettingsAPI";
import { buildQuoteEmailHtml } from "./Preventivi";
import { italianErrorMessage } from "../utils/italianErrorMessage";

const PRACTICE_TYPES = [
  { value: "EVENTO", labelKey: "evento" },
  { value: "PRENOTAZIONE", labelKey: "prenotazione" },
  { value: "PREVENTIVO", labelKey: "preventivo" },
];

const capitalizeFirst = (value) => {
  const text = String(value || "");
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
};

const setupImageSrc = (filePath) => {
  if (!filePath) return "";
  if (/^https?:\/\//i.test(filePath)) return filePath;
  return filePath.startsWith("/") ? filePath : `/${filePath}`;
};

const STATUS_LABEL_KEYS = {
  PREVENTIVO: "preventivo",
  TO_CONFIRM: "daconfermare",
  CONFIRMED: "confermata",
  CHECKED_IN: "checkIn",
  EXPIRED_QUOTE: "preventivoScaduto",
  COMPLETED: "completata",
  CANCELLED: "annullata",
  NO_SHOW: "noshow",
};

const SEARCHABLE_STATUS_ENTRIES = Object.entries(STATUS_LABEL_KEYS)
  .filter(([value]) => !["PREVENTIVO", "EXPIRED_QUOTE"].includes(value));

const EMPTY_DAY = {
  date: "",
  startTime: "09:00",
  endTime: "10:00",
  guests: 1,
  tableIds: [],
  tableSetups: [],
  packageItems: [],
  serviceItems: [],
  note: "",
};

const EMPTY_ROW = {
  type: "custom",
  refId: "",
  description: "",
  orario: "",
  note: "",
  departmentId: "",
  quantity: 1,
  unitPrice: 0,
  dayIndex: "all",
};

const PLANNER_MODES = [
  { value: "DAY", labelKey: "giorno" },
  { value: "THREE_DAYS", labelKey: "tregiorni" },
  { value: "SEVEN_DAYS", labelKey: "settegiorni" },
  { value: "WORK_WEEK", labelKey: "settlavorativa" },
  { value: "WEEK", labelKey: "settimana" },
  { value: "AGENDA", labelKey: "agenda" },
];

const PREPAYMENT_PAYMENT_METHODS = [
  { value: "CASH", labelKey: "cash" },
  { value: "CARD", labelKey: "card" },
  { value: "BANK_TRANSFER", labelKey: "bankTransfer" },
  { value: "VOUCHER", labelKey: "voucher" },
  { value: "ROOM_CHARGE", labelKey: "roomCharge" },
];

const PREPAYMENT_ADVANCE_DOCUMENT_TYPES = [
  { value: "COMMERCIAL_DOCUMENT", labelKey: "commercialDocument" },
  { value: "INVOICE_TD02", labelKey: "invoiceTd02" },
];

const PREPAYMENT_REFUND_DOCUMENT_TYPES = [
  { value: "PLAIN_RECEIPT", labelKey: "plainReceiptRefund" },
  { value: "COMMERCIAL_CANCELLATION", labelKey: "commercialCancellation" },
  { value: "COMMERCIAL_RETURN", labelKey: "commercialReturn" },
  { value: "CREDIT_NOTE_TD04", labelKey: "creditNoteTd04" },
];

const PREPAYMENT_VAT_NATURE_OPTIONS = ["N1", "N2", "N3", "N4", "N5", "N6", "N7"];

function refundDocumentTypesForMovement(movement) {
  if (movement?.documentType === "COMMERCIAL_DOCUMENT") {
    return PREPAYMENT_REFUND_DOCUMENT_TYPES.filter((item) => (
      item.value === "COMMERCIAL_CANCELLATION" || item.value === "COMMERCIAL_RETURN"
    ));
  }
  if (movement?.documentType === "INVOICE_TD02") {
    return PREPAYMENT_REFUND_DOCUMENT_TYPES.filter((item) => item.value === "CREDIT_NOTE_TD04");
  }
  return PREPAYMENT_REFUND_DOCUMENT_TYPES.filter((item) => item.value === "PLAIN_RECEIPT");
}

function prepaymentDocumentTypeLabel(value, t) {
  const option = [
    ...PREPAYMENT_ADVANCE_DOCUMENT_TYPES,
    ...PREPAYMENT_REFUND_DOCUMENT_TYPES,
  ].find((item) => item.value === value);

  if (option) {
    return t(`congressReservations.prepayments.documentTypes.${option.labelKey}`);
  }

  return value ? String(value).replace(/_/g, " ").toLowerCase() : "-";
}

function prepaymentDeliverySeverity(delivery) {
  if (delivery?.status === "FAILED") return "warning";
  if (delivery?.status === "SKIPPED") return "info";
  return "success";
}

const DAY_START_HOUR = 7;
const DAY_END_HOUR = 23;

function pad(value) {
  return String(value).padStart(2, "0");
}

function toLocalDateInput(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function localCalendarDateKey(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : toLocalDateInput(date);
}

function isEventArrivalToday(days) {
  const arrivalDate = (Array.isArray(days) ? days : [])
    .map((day) => localCalendarDateKey(day?.date || day?.dayDate || day?.startDate))
    .filter(Boolean)
    .sort()[0];
  return Boolean(arrivalDate) && arrivalDate === toLocalDateInput(new Date());
}

function toDateTimeIso(date, time) {
  if (!date || !time) return null;
  const d = new Date(`${date}T${time}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function parseTimePickerValue(value) {
  if (!value || typeof value !== "string") return null;
  const [hours, minutes] = value.split(":").map(Number);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}

function formatTimePickerValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function timeToMinutes(value) {
  const match = String(value || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function isEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function isTimeInsideRange(time, start, end) {
  const valueMinutes = timeToMinutes(time);
  const startMinutes = timeToMinutes(start);
  const endMinutes = timeToMinutes(end);
  if (valueMinutes === null || startMinutes === null || endMinutes === null) return false;
  if (startMinutes === endMinutes) return true;
  return startMinutes < endMinutes
    ? valueMinutes >= startMinutes && valueMinutes <= endMinutes
    : valueMinutes >= startMinutes || valueMinutes <= endMinutes;
}

function targetDayIndexesForRow(row, dayCount) {
  if (row.dayIndex === "all") {
    return Array.from({ length: dayCount }, (_, index) => index);
  }
  const dayIndex = Number(row.dayIndex);
  return Number.isInteger(dayIndex) && dayIndex >= 0 && dayIndex < dayCount ? [dayIndex] : [];
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function addHours(date, hours) {
  const d = new Date(date);
  d.setHours(d.getHours() + hours, 0, 0, 0);
  return d;
}

function startOfWeekMonday(date) {
  const d = new Date(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getDateLocale(language) {
  if (language?.startsWith("sq")) return "sq-AL";
  if (language?.startsWith("en")) return "en-US";
  return "it-IT";
}

function formatDayLabel(date, language) {
  return new Intl.DateTimeFormat(getDateLocale(language), {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  }).format(date);
}

function formatDateLabel(date, language) {
  return new Intl.DateTimeFormat(getDateLocale(language), { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).format(date);
}

function formatFormDayLabel(dateValue, language) {
  if (!dateValue) return "";
  const [year, month, day] = String(dateValue).split("-").map(Number);
  const date = Number.isInteger(year) && Number.isInteger(month) && Number.isInteger(day)
    ? new Date(year, month - 1, day, 12, 0, 0, 0)
    : new Date(dateValue);
  if (Number.isNaN(date.getTime())) return String(dateValue);
  return new Intl.DateTimeFormat(getDateLocale(language), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatTimeLabel(date, language) {
  return new Intl.DateTimeFormat(getDateLocale(language), { hour: "2-digit", minute: "2-digit" }).format(date);
}
function timeInputFromDate(date) {
  if (!date) return "00:00";
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
function formatDateTime(value, language) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return new Intl.DateTimeFormat(getDateLocale(language), {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function formatMoney(value) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(value) || 0);
}

function moneyValue(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function eventPracticeTotal(event) {
  return (event?.days || []).reduce((sum, day) => {
    const rows = [
      ...(day.packages || []),
      ...(day.services || []),
      ...(day.customRows || []),
    ];
    return sum + rows.reduce((rowSum, item) => (
      rowSum + moneyValue(item.quantity || 0) * moneyValue(item.unitPrice || 0)
    ), 0);
  }, 0);
}

function getEventPrepaymentTotals(event, localTotals) {
  const totals = localTotals?.[event?.id] || event?.prepaymentTotals || event?.prepaymentsTotals || {};
  const practiceTotal = moneyValue(totals.practiceTotal ?? eventPracticeTotal(event));
  const totalDeposits = moneyValue(totals.totalDeposits ?? event?.totalDeposits ?? 0);
  const totalAdvances = moneyValue(totals.totalAdvances ?? event?.totalAdvances ?? 0);
  const totalPrepayments = moneyValue(totals.totalPrepayments ?? totals.availablePrepayments ?? event?.totalPrepayments ?? 0);
  return {
    practiceTotal,
    totalDeposits,
    totalAdvances,
    totalPrepayments,
    amountDue: moneyValue(totals.amountDue ?? Math.max(0, practiceTotal - totalPrepayments)),
  };
}

function hasEventPrepaymentTotals(event, localTotals) {
  return Boolean(localTotals?.[event?.id] || event?.prepaymentTotals || event?.prepaymentsTotals);
}

function normalizePrepaymentMovements(event, localMovements) {
  const serverMovements = Array.isArray(event?.prepayments)
    ? event.prepayments
    : Array.isArray(event?.prepaymentMovements)
      ? event.prepaymentMovements
      : [];
  const sessionMovements = Array.isArray(localMovements?.[event?.id]) ? localMovements[event.id] : [];
  const byId = new Map();
  [...serverMovements, ...sessionMovements].forEach((movement) => {
    if (movement?.id) byId.set(Number(movement.id), movement);
  });
  return Array.from(byId.values()).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
}

function prepaymentMovementAvailableAmount(source, movements) {
  if (!source?.id || source.operation !== "RECEIPT") return 0;
  if (source.availableAmount !== undefined && source.availableAmount !== null) {
    return Math.max(0, moneyValue(source.availableAmount));
  }
  const consumed = (movements || [])
    .filter((movement) => Number(movement.sourceMovementId) === Number(source.id) && movement.operation !== "RECEIPT")
    .reduce((sum, movement) => sum + moneyValue(movement.amount), 0);
  return Math.max(0, moneyValue(source.amount) - consumed);
}

function eventRecipient(event) {
  const names = [];
  const fullName = `${event?.client?.name || ""} ${event?.client?.surname || ""}`.trim();
  if (fullName) names.push(fullName);
  if (event?.company?.companyName) names.push(event.company.companyName);
  if (names.length > 0) return names.join(" / ");
  return event?.genericRecipientName || event?.quoteEmail || "-";
}

function collectTableNames(event) {
  const names = [];
  (event?.days || []).forEach((day) => {
    (day.tableLinks || []).forEach((link) => {
      if (link?.table?.name) {
        const setup = link.setup?.name ? ` (${link.setup.name})` : "";
        names.push(`${link.table.name}${setup}`);
      }
    });
  });
  return [...new Set(names)];
}

function usageTypeFromTableLink(link) {
  if (link?.usageType?.name) return link.usageType;
  const usageTypeId = Number(link?.usageTypeId);
  return Number.isInteger(usageTypeId) && usageTypeId > 0
    ? { id: usageTypeId, name: `#${usageTypeId}` }
    : null;
}

function collectUsageTypeNames(event) {
  const names = [];
  (event?.days || []).forEach((day) => {
    (day.tableLinks || []).forEach((link) => {
      const usageType = usageTypeFromTableLink(link);
      if (usageType?.name) names.push(usageType.name);
    });
  });
  return [...new Set(names)];
}

function productCategoryIds(product) {
  return (product?.categories || [])
    .map((item) => Number(item.categoryId ?? item.category?.id))
    .filter((id) => Number.isInteger(id) && id > 0);
}

function productMatchesCategories(product, categoryIds = []) {
  if (!categoryIds.length) return true;
  const ids = productCategoryIds(product);
  return ids.some((id) => categoryIds.includes(id));
}

function packageMatchesCategories(pkg, categoryIds = []) {
  if (!categoryIds.length) return true;
  return (pkg?.items || []).some((item) => productMatchesCategories(item.product, categoryIds));
}

function eventMatchesCategories(event, categoryIds = []) {
  if (!categoryIds.length) return true;
  return (event?.days || []).some((day) => (
    (day.services || []).some((item) => productMatchesCategories(item.product, categoryIds)) ||
    (day.packages || []).some((item) => packageMatchesCategories(item.package, categoryIds))
  ));
}

function tableAreaName(table) {
  return table?.room?.name || table?.area?.name || table?.areaName || "";
}

function relatedTableIdsFor(table, tableById) {
  if (!table) return [];
  const ids = [];
  if (table.parentTableId) ids.push(Number(table.parentTableId));
  (table.childTables || []).forEach((child) => ids.push(Number(child.id)));
  if (!table.childTables?.length) {
    tableById.forEach((candidate) => {
      if (Number(candidate.parentTableId) === Number(table.id)) ids.push(Number(candidate.id));
    });
  }
  return [...new Set(ids.filter((id) => Number.isInteger(id) && id > 0))];
}

function findEventDayForTableDay(event, tableId, day, tableById, includeRelated = false) {
  const dayKey = toLocalDateInput(day);
  const directMatch = (event?.days || []).find((eventDay) => {
    const sameDay = toLocalDateInput(eventDay.startDate) === dayKey;
    const hasTable = (eventDay.tableLinks || []).some((link) => Number(link.tableId) === Number(tableId));
    return sameDay && hasTable;
  });
  if (directMatch) return { day: directMatch, isRelatedBlock: false, sourceTable: tableById.get(Number(tableId)) };
  if (!includeRelated) return null;

  const relatedIds = relatedTableIdsFor(tableById.get(Number(tableId)), tableById);
  if (relatedIds.length === 0) return null;
  const relatedMatch = (event?.days || []).find((eventDay) => {
    const sameDay = toLocalDateInput(eventDay.startDate) === dayKey;
    const hasTable = (eventDay.tableLinks || []).some((link) => relatedIds.includes(Number(link.tableId)));
    return sameDay && hasTable;
  });
  if (!relatedMatch) return null;
  const sourceLink = (relatedMatch.tableLinks || []).find((link) => relatedIds.includes(Number(link.tableId)));
  return {
    day: relatedMatch,
    isRelatedBlock: true,
    sourceTable: tableById.get(Number(sourceLink?.tableId)),
  };
}

function eventStartForTableDay(event, tableId, day, tableById, includeRelated = false) {
  return findEventDayForTableDay(event, tableId, day, tableById, includeRelated)?.day?.startDate || "";
}

function eventDayForTableDay(event, tableId, day, tableById, includeRelated = false) {
  return findEventDayForTableDay(event, tableId, day, tableById, includeRelated)?.day;
}

function normalizeCatalogRows(packages, products) {
  const packageRows = (packages || []).map((item) => ({
    type: "package",
    refId: item.id,
    label: item.name,
    description: item.description || item.name,
    unitPrice: Number(item.price || item.manualPrice || 0),
  }));
  const productRows = (products || []).map((item) => ({
    type: "service",
    refId: item.id,
    label: item.name,
    description: item.name,
    unitPrice: Number(item.price || 0),
  }));
  return [...packageRows, ...productRows];
}

export default function CongressReservationsPage() {
  const { t, i18n } = useTranslation();
const language = i18n.language;
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const tenantType = localStorage.getItem("tenantType");
  const isRestaurantTenant = String(tenantType || "").toUpperCase() === "RISTORANTE";
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const surfaceBg = theme.palette.background.default;
  const surfacePaper = theme.palette.background.paper;
  const softHeaderBg = theme.palette.mode === "dark" ? "#2A241C" : "#FFF7ED";
  const softCellBg = theme.palette.mode === "dark" ? "#1D2328" : "#F8FAFC";
  const softGridBg = `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.96)} 0%, ${alpha(theme.palette.background.default, 0.98)} 100%)`;
  const softGridHover = alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.12 : 0.1);
  const borderColor = alpha(theme.palette.divider, theme.palette.mode === "dark" ? 1 : 0.9);
  const subtleBorder = alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.9 : 0.7);

  const preventivoBg = alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.18 : 0.12);
  const preventivoBorder = alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.5 : 0.55);
  const infoBg = alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.18 : 0.12);
  const infoBorder = alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.45 : 0.45);
  const relatedBg = alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.08 : 0.05);
  const relatedBorder = alpha(theme.palette.text.secondary, theme.palette.mode === "dark" ? 0.45 : 0.4);
  const eventShadow = theme.palette.mode === "dark"
    ? `0 8px 18px ${alpha("#000", 0.3)}`
    : `0 8px 18px ${alpha(theme.palette.warning.main, 0.16)}`;

  const [loading, setLoading] = useState(false);
  const [savingPractice, setSavingPractice] = useState(false);
  const [error, setError] = useState("");
  const [tables, setTables] = useState([]);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [packages, setPackages] = useState([]);
  const [setups, setSetups] = useState([]);
  const [products, setProducts] = useState([]);
  const [usageTypes, setUsageTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentScope, setDepartmentScope] = useState({ canSeeAllDepartments: true, departmentIds: [], departments: [] });
  const [events, setEvents] = useState([]);
  const [eventColors, setEventColors] = useState({
    confirmed: "#2E7D32",
    inProgress: "#F59E0B",
    completed: "#16A34A",
    expiredQuote: "#DC2626",
  });
  const [tenantLogoBase64, setTenantLogoBase64] = useState("");
  const [nowTick, setNowTick] = useState(() => new Date());
  const [plannerDate, setPlannerDate] = useState(new Date());
  const [plannerMode, setPlannerMode] = useState("WEEK");
  const [filters, setFilters] = useState({
    status: "",
    tableId: "",
    recipient: "",
    from: "",
    to: "",
    categoryIds: [],
    hidePrices: false,
    showCancelledOnBoard: false,
  });
  const [practiceSummaryFilters, setPracticeSummaryFilters] = useState({
    q: "",
    status: "",
    practiceType: "",
    tableId: "",
  });
  const [practiceSummaryPage, setPracticeSummaryPage] = useState(0);
  const [practiceSummaryRowsPerPage, setPracticeSummaryRowsPerPage] = useState(10);
  const [practiceSummarySort, setPracticeSummarySort] = useState({ key: "updatedAt", direction: "desc" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState({
    practiceType: "PREVENTIVO",
    eventName: "",
    status: "PREVENTIVO",
    client: null,
    company: null,
    genericRecipientName: "",
    genericRecipientVatNumber: "",
    quoteEmail: "",
    clientEmailActive: false,
    companyEmailActive: false,
    quoteValidUntil: "",
    cancellationReason: "",
    note: "",
    days: [{ ...EMPTY_DAY }],
    rows: [{ ...EMPTY_ROW }],
    sendEmail: false,
    includeLogo: false,
    emailMessage: "",
    attachments: [],
    newAttachments: [],
  });
  const [setupManagerTable, setSetupManagerTable] = useState(null);
  const [setupManagerSetupIds, setSetupManagerSetupIds] = useState([]);
  const [summaryEmailOpen, setSummaryEmailOpen] = useState(false);
  const [summaryRecipients, setSummaryRecipients] = useState([]);
  const [summaryRecipientDraft, setSummaryRecipientDraft] = useState("");
  const [cancelReasonOpen, setCancelReasonOpen] = useState(false);
  const [cancelReasonError, setCancelReasonError] = useState("");
  const [practiceConfirmDialog, setPracticeConfirmDialog] = useState(null);
  const practiceConfirmResolverRef = useRef(null);
  const [serviceAvailabilityByDate, setServiceAvailabilityByDate] = useState({});
  const [serviceAvailabilityError, setServiceAvailabilityError] = useState("");
  const [prepaymentDialogEvent, setPrepaymentDialogEvent] = useState(null);
  const [prepaymentOperation, setPrepaymentOperation] = useState("RECEIVE");
  const [prepaymentForm, setPrepaymentForm] = useState({
    type: "DEPOSIT",
    amount: "",
    vatRate: "10",
    paymentMethod: "CASH",
    documentType: "PLAIN_RECEIPT",
    invoiceId: "",
    vatNature: "",
    documentNumber: "",
    documentDate: toLocalDateInput(new Date()),
    description: "",
    note: "",
    sourceMovementId: "",
  });
  const [savingPrepayment, setSavingPrepayment] = useState(false);
  const [loadingPrepaymentSummary, setLoadingPrepaymentSummary] = useState(false);
  const [prepaymentSummariesLoading, setPrepaymentSummariesLoading] = useState(false);
  const [prepaymentTotalsByEventId, setPrepaymentTotalsByEventId] = useState({});
  const [prepaymentMovementsByEventId, setPrepaymentMovementsByEventId] = useState({});
  const [proformaDialogEvent, setProformaDialogEvent] = useState(null);
  const [proformaSelectedDayIds, setProformaSelectedDayIds] = useState([]);
  const [proformaSelectedRowKeys, setProformaSelectedRowKeys] = useState([]);
  const [proformaDownloading, setProformaDownloading] = useState(false);

  const plannerDays = useMemo(() => {
    if (plannerMode === "DAY" || plannerMode === "AGENDA") return [plannerDate];
    if (plannerMode === "THREE_DAYS") return Array.from({ length: 3 }, (_, index) => addDays(plannerDate, index));
    if (plannerMode === "SEVEN_DAYS") return Array.from({ length: 7 }, (_, index) => addDays(plannerDate, index));
    const start = startOfWeekMonday(plannerDate);
    const length = plannerMode === "WORK_WEEK" ? 5 : 7;
    return Array.from({ length }, (_, index) => addDays(start, index));
  }, [plannerDate, plannerMode]);

  const dayTimeSlots = useMemo(() => {
    const base = new Date(plannerDate);
    base.setHours(DAY_START_HOUR, 0, 0, 0);
    return Array.from({ length: DAY_END_HOUR - DAY_START_HOUR }, (_, index) => addHours(base, index));
  }, [plannerDate]);

  const plannerStepDays = plannerMode === "DAY" || plannerMode === "AGENDA"
    ? 1
    : plannerMode === "THREE_DAYS"
      ? 3
      : 7;

  const catalogRows = useMemo(() => normalizeCatalogRows(packages, products), [packages, products]);
  const categoryOptions = useMemo(() => {
    const byId = new Map();
    (products || []).forEach((product) => {
      (product.categories || []).forEach((entry) => {
        const id = Number(entry.categoryId ?? entry.category?.id);
        const name = entry.category?.name || product.categoryName || `Categoria ${id}`;
        if (Number.isInteger(id) && id > 0 && !byId.has(id)) {
          byId.set(id, { id, name });
        }
      });
      const fallbackId = Number(product.categoryId);
      if (Number.isInteger(fallbackId) && fallbackId > 0 && !byId.has(fallbackId)) {
        byId.set(fallbackId, { id: fallbackId, name: product.categoryName || `Categoria ${fallbackId}` });
      }
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);
  const restrictedDepartmentIds = useMemo(() => {
    if (departmentScope?.canSeeAllDepartments) return [];
    return (departmentScope?.departmentIds || []).map(Number).filter((id) => Number.isInteger(id) && id > 0);
  }, [departmentScope]);
  const isDepartmentRestricted = restrictedDepartmentIds.length > 0;
  const restrictedDepartmentNames = useMemo(
    () => (departmentScope?.departments || []).map((item) => item.name).filter(Boolean).join(", "),
    [departmentScope],
  );
  const rowMatchesDepartmentScope = useCallback((row) => {
    if (!isDepartmentRestricted) return true;
    return restrictedDepartmentIds.includes(Number(row?.departmentId));
  }, [isDepartmentRestricted, restrictedDepartmentIds]);
  const eventMatchesDepartmentScope = useCallback((event) => {
    if (!isDepartmentRestricted) return true;
    return (event.days || []).some((day) => (
      [...(day.packages || []), ...(day.services || []), ...(day.customRows || [])]
        .some((row) => restrictedDepartmentIds.includes(Number(row.departmentId)))
    ));
  }, [isDepartmentRestricted, restrictedDepartmentIds]);
  const departmentScopeSummary = useCallback((event) => {
    if (!isDepartmentRestricted) return "";
    let count = 0;
    (event.days || []).forEach((day) => {
      [...(day.packages || []), ...(day.services || []), ...(day.customRows || [])].forEach((row) => {
        if (restrictedDepartmentIds.includes(Number(row.departmentId))) count += 1;
      });
    });
    return count > 0 ? `${count} righe reparto` : "";
  }, [isDepartmentRestricted, restrictedDepartmentIds]);
  const defaultDepartmentId = restrictedDepartmentIds[0] || "";
  const createEmptyRow = useCallback(() => ({ ...EMPTY_ROW, departmentId: defaultDepartmentId }), [defaultDepartmentId]);
  const visibleFormRows = useMemo(
    () => form.rows.map((row, index) => ({ row, index })).filter(({ row }) => rowMatchesDepartmentScope(row)),
    [form.rows, rowMatchesDepartmentScope],
  );
  const hiddenFormRowsCount = Math.max(0, form.rows.length - visibleFormRows.length);
  const formServicesTotal = useMemo(() => (
    (form.rows || []).reduce((sum, row) => (
      sum + moneyValue(row.quantity || 0) * moneyValue(row.unitPrice || 0)
    ), 0)
  ), [form.rows]);
  const checkedInStartDayIndex = useMemo(() => {
    if (editingEvent?.status !== "CHECKED_IN") return -1;
    let earliestIndex = -1;
    let earliestTime = Number.POSITIVE_INFINITY;
    (editingEvent.days || []).forEach((day, index) => {
      const time = new Date(day?.startDate).getTime();
      if (!Number.isNaN(time) && time < earliestTime) {
        earliestTime = time;
        earliestIndex = index;
      }
    });
    return earliestIndex;
  }, [editingEvent]);
  const checkedInStartDate = checkedInStartDayIndex >= 0
    ? toLocalDateInput(editingEvent.days[checkedInStartDayIndex]?.startDate)
    : "";

  const visibleEvents = useMemo(() => {
    const selectedCategoryIds = Array.isArray(filters.categoryIds) ? filters.categoryIds.map(Number).filter(Boolean) : [];
    return events.filter((event) => {
      if (!eventMatchesDepartmentScope(event)) return false;
      if (filters.status && event.status !== filters.status) return false;
      if (filters.tableId) {
        const targetId = Number(filters.tableId);
        const hasTable = (event.days || []).some((day) =>
          (day.tableLinks || []).some((link) => Number(link.tableId) === targetId),
        );
        if (!hasTable) return false;
      }
      if (filters.recipient.trim()) {
        const haystack = `${event.eventName || ""} ${eventRecipient(event)}`.toLowerCase();
        if (!haystack.includes(filters.recipient.trim().toLowerCase())) return false;
      }
      if (filters.from) {
        const from = new Date(`${filters.from}T00:00:00`);
        const hasAfter = (event.days || []).some((day) => new Date(day.endDate) >= from);
        if (!hasAfter) return false;
      }
      if (filters.to) {
        const to = new Date(`${filters.to}T23:59:59`);
        const hasBefore = (event.days || []).some((day) => new Date(day.startDate) <= to);
        if (!hasBefore) return false;
      }
      if (selectedCategoryIds.length > 0 && !eventMatchesCategories(event, selectedCategoryIds)) return false;
      return true;
    });
  }, [events, filters, eventMatchesDepartmentScope]);

  const boardEvents = useMemo(() => {
    return visibleEvents.filter((event) => filters.showCancelledOnBoard || event.status !== "CANCELLED");
  }, [filters.showCancelledOnBoard, visibleEvents]);

  const boardTables = useMemo(() => {
    return [...tables].sort((a, b) => {
      const areaCompare = tableAreaName(a).localeCompare(tableAreaName(b), "it", { sensitivity: "base" });
      if (areaCompare !== 0) return areaCompare;
      return String(a.name || "").localeCompare(String(b.name || ""), "it", { sensitivity: "base", numeric: true });
    });
  }, [tables]);

  const tableById = useMemo(() => {
    const map = new Map();
    tables.forEach((table) => map.set(Number(table.id), table));
    return map;
  }, [tables]);

  const practiceSummaryEvents = useMemo(() => {
    const q = practiceSummaryFilters.q.trim().toLowerCase();
    const tableId = Number(practiceSummaryFilters.tableId);

    const sortValue = (event) => {
      switch (practiceSummarySort.key) {
        case "practice": return `${event.eventName || ""} ${event.practiceType || ""}`.trim();
        case "status": return event.status || "";
        case "recipient": return eventRecipient(event);
        case "table": return collectTableNames(event).join(", ");
        case "period": return new Date(event.days?.[0]?.startDate || 0).getTime();
        case "sendCount": return Number(event.quoteSendCount || 0);
        case "prepayments": return getEventPrepaymentTotals(event, prepaymentTotalsByEventId).amountDue;
        default: return new Date(event.updatedAt || event.createdAt || 0).getTime();
      }
    };

    const compareValues = (left, right) => {
      if (typeof left === "number" && typeof right === "number") return left - right;
      return String(left || "").localeCompare(String(right || ""), "it", {
        sensitivity: "base",
        numeric: true,
      });
    };

    return [...events]
      .filter((event) => {
        if (!eventMatchesDepartmentScope(event)) return false;
        if (practiceSummaryFilters.status) {
          if (event.status !== practiceSummaryFilters.status) return false;
        } else if (event.status === "COMPLETED") {
          return false;
        }
        if (practiceSummaryFilters.practiceType && event.practiceType !== practiceSummaryFilters.practiceType) return false;
        if (Number.isInteger(tableId) && tableId > 0) {
          const hasTable = (event.days || []).some((day) =>
            (day.tableLinks || []).some((link) => Number(link.tableId) === tableId),
          );
          if (!hasTable) return false;
        }
        if (q) {
          const tableNames = collectTableNames(event).join(" ");
          const haystack = [
            event.eventName,
            eventRecipient(event),
            event.quoteEmail,
            event.note,
            tableNames,
          ].filter(Boolean).join(" ").toLowerCase();
          if (!haystack.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const comparison = compareValues(sortValue(a), sortValue(b));
        if (comparison !== 0) return practiceSummarySort.direction === "asc" ? comparison : -comparison;
        return Number(a.id || 0) - Number(b.id || 0);
      });
  }, [events, practiceSummaryFilters, practiceSummarySort, eventMatchesDepartmentScope, prepaymentTotalsByEventId]);

  const handlePracticeSummarySort = (key) => {
    setPracticeSummarySort((previous) => ({
      key,
      direction: previous.key === key && previous.direction === "asc" ? "desc" : "asc",
    }));
  };

  const pagedPracticeSummaryEvents = useMemo(() => {
    const start = practiceSummaryPage * practiceSummaryRowsPerPage;
    return practiceSummaryEvents.slice(start, start + practiceSummaryRowsPerPage);
  }, [practiceSummaryEvents, practiceSummaryPage, practiceSummaryRowsPerPage]);

  useEffect(() => {
    setPracticeSummaryPage(0);
  }, [practiceSummaryFilters, practiceSummaryRowsPerPage]);

  const preloadPrepaymentSummaries = useCallback(async (sourceEvents) => {
    const eventIds = [...new Set((Array.isArray(sourceEvents) ? sourceEvents : [])
      .map((event) => Number(event?.id))
      .filter((eventId) => Number.isInteger(eventId) && eventId > 0))];
    if (eventIds.length === 0) return;

    setPrepaymentSummariesLoading(true);
    const results = await Promise.allSettled(eventIds.map(async (eventId) => {
      const summary = await getReservationEventPrepaymentSummary(eventId, token, tenant);
      return { eventId, summary };
    }));

    const totalsByEventId = {};
    const movementsByEventId = {};
    results.forEach((result) => {
      if (result.status !== "fulfilled") return;
      const { eventId, summary } = result.value;
      totalsByEventId[eventId] = summary?.totals || {};
      movementsByEventId[eventId] = Array.isArray(summary?.movements) ? summary.movements : [];
    });

    if (Object.keys(totalsByEventId).length > 0) {
      setPrepaymentTotalsByEventId((prev) => ({ ...prev, ...totalsByEventId }));
    }
    if (Object.keys(movementsByEventId).length > 0) {
      setPrepaymentMovementsByEventId((prev) => ({ ...prev, ...movementsByEventId }));
    }
    setPrepaymentSummariesLoading(false);
  }, [tenant, token]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [tableData, clientData, companyData, packageData, setupData, productData, usageTypeData, departmentData, departmentScopeData, eventData, settingsData] = await Promise.all([
        getAllTavoli(token, tenant, { noCache: true }),
        getAllClienti(token, tenant),
        getAllCompanies(token, tenant),
        getAllPackages(token, tenant),
        getAllSetups(token, tenant),
        getAllProdotti(token, tenant),
        getAllUsageTypes(token, tenant),
        getAllDepartments(token, tenant),
        getMyDepartmentScope(token, tenant),
        getReservationEvents(token, tenant),
        getTenantSettings(token, tenant),
      ]);
      setTables(Array.isArray(tableData) ? tableData : []);
      setClients(Array.isArray(clientData) ? clientData : []);
      setCompanies(Array.isArray(companyData) ? companyData : []);
      setPackages(Array.isArray(packageData) ? packageData : []);
      setSetups(Array.isArray(setupData) ? setupData : []);
      setProducts(Array.isArray(productData) ? productData : []);
      setUsageTypes(Array.isArray(usageTypeData) ? usageTypeData : []);
      setDepartments(Array.isArray(departmentData) ? departmentData : []);
      setDepartmentScope(departmentScopeData || { canSeeAllDepartments: true, departmentIds: [], departments: [] });
      const nextEvents = Array.isArray(eventData) ? eventData : [];
      setEvents(nextEvents);
      preloadPrepaymentSummaries(nextEvents);
      setEventColors({
        confirmed: settingsData?.eventConfirmedColor || "#2E7D32",
        inProgress: settingsData?.eventInProgressColor || "#F59E0B",
        completed: settingsData?.eventCompletedColor || "#16A34A",
        expiredQuote: settingsData?.quoteExpiredColor || "#DC2626",
      });
      setTenantLogoBase64(
        settingsData?.logoBase64
        || settingsData?.tenantLogoBase64
        || settingsData?.logo
        || "",
      );
    } catch (err) {
      setError(italianErrorMessage(err, t("congressReservations.loadError")));
    } finally {
      setLoading(false);
    }
  }, [preloadPrepaymentSummaries, tenant, token, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const serviceAvailabilityDateKey = useMemo(
    () => [...new Set((form.days || []).map((day) => day.date).filter(Boolean))].sort().join("|"),
    [form.days],
  );

  useEffect(() => {
    if (!dialogOpen || !serviceAvailabilityDateKey) {
      setServiceAvailabilityByDate({});
      setServiceAvailabilityError("");
      return;
    }

    let active = true;
    const dates = serviceAvailabilityDateKey.split("|").filter(Boolean);
    Promise.all(
      dates.map(async (date) => {
        const data = await getProductServiceAvailability(token, tenant, {
          date,
          eventId: editingEvent?.id,
        });
        return [date, Array.isArray(data) ? data : []];
      }),
    )
      .then((entries) => {
        if (!active) return;
        setServiceAvailabilityByDate(Object.fromEntries(entries));
        setServiceAvailabilityError("");
      })
      .catch((err) => {
        if (!active) return;
        setServiceAvailabilityByDate({});
        setServiceAvailabilityError(italianErrorMessage(
          err,
          t("congressReservations.availabilityLoadError", { defaultValue: "Errore caricamento disponibilità servizi" }),
        ));
      });

    return () => {
      active = false;
    };
  }, [dialogOpen, serviceAvailabilityDateKey, editingEvent?.id, token, tenant, t]);

  useEffect(() => {
    const timer = setInterval(() => setNowTick(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener("reservation_event_created", handler);
    window.addEventListener("reservation_event_updated", handler);
    window.addEventListener("reservation_event_deleted", handler);
    return () => {
      window.removeEventListener("reservation_event_created", handler);
      window.removeEventListener("reservation_event_updated", handler);
      window.removeEventListener("reservation_event_deleted", handler);
    };
  }, [loadData]);

  const resetForm = () => {
    setEditingEvent(null);
    setForm({
      practiceType: "PREVENTIVO",
      eventName: "",
      status: "PREVENTIVO",
      client: null,
      company: null,
      genericRecipientName: "",
      genericRecipientVatNumber: "",
      quoteEmail: "",
      clientEmailActive: false,
      companyEmailActive: false,
      quoteValidUntil: "",
      cancellationReason: "",
      note: "",
      days: [{ ...EMPTY_DAY }],
      rows: [createEmptyRow()],
      sendEmail: false,
      includeLogo: false,
      emailMessage: "",
      attachments: [],
      newAttachments: [],
    });
  };

  const openCreateFromSlot = (table, day, startTime = "09:00", endTime = "10:00") => {
    resetForm();
    setForm((prev) => ({
      ...prev,
      eventName: "",
      days: [{
        ...EMPTY_DAY,
        date: toLocalDateInput(day),
        startTime,
        endTime,
        tableIds: table?.id ? [table.id] : [],
      }],
    }));
    setDialogOpen(true);
  };

  useEffect(() => {
    const openEmptyPractice = () => {
      resetForm();
      setDialogOpen(true);
    };
    window.addEventListener("open_congress_practice", openEmptyPractice);
    if (location.state?.openPractice || new URLSearchParams(location.search).get("newPractice") === "1") {
      openEmptyPractice();
      navigate(location.pathname, { replace: true, state: {} });
    }
    return () => window.removeEventListener("open_congress_practice", openEmptyPractice);
  }, [location.pathname, location.search, location.state, navigate]);

  const openEditEvent = (event) => {
    const rows = [];
    (event.days || []).forEach((day, dayIndex) => {
      (day.packages || []).forEach((item) => rows.push({
        type: "package",
        refId: item.packageId,
        description: item.description || item.package?.name || "Pacchetto",
        orario: item.orario || "",
        note: item.note || "",
        departmentId: item.departmentId || "",
        quantity: Number(item.quantity || 1),
        unitPrice: Number(item.unitPrice ?? item.package?.price ?? 0),
        dayIndex,
      }));
      (day.services || []).forEach((item) => rows.push({
        type: "service",
        refId: item.productId,
        description: item.description || item.product?.name || "Servizio",
        orario: item.orario || "",
        note: item.note || "",
        departmentId: item.departmentId || "",
        quantity: Number(item.quantity || 1),
        unitPrice: Number(item.unitPrice ?? item.product?.price ?? 0),
        dayIndex,
      }));
      (day.customRows || []).forEach((item) => rows.push({
        type: "custom",
        refId: "",
        description: item.description || "",
        orario: "",
        note: item.note || "",
        departmentId: item.departmentId || "",
        quantity: Number(item.quantity || 1),
        unitPrice: Number(item.unitPrice || 0),
        dayIndex,
      }));
    });

    setEditingEvent(event);
    setForm({
      practiceType: event.practiceType || (event.status === "PREVENTIVO" ? "PREVENTIVO" : "PRENOTAZIONE"),
      eventName: event.eventName || "",
      status: event.status || "PREVENTIVO",
      client: event.client || null,
      company: event.company || null,
      genericRecipientName: event.genericRecipientName || "",
      genericRecipientVatNumber: event.genericRecipientVatNumber || "",
      quoteEmail: event.quoteEmail || "",
      clientEmailActive: Boolean(event.clientEmailActive),
      companyEmailActive: Boolean(event.companyEmailActive),
      quoteValidUntil: event.quoteValidUntil ? toLocalDateInput(event.quoteValidUntil) : "",
      cancellationReason: event.cancellationReason || "",
      note: event.note || "",
      days: (event.days || []).map((day) => ({
        date: toLocalDateInput(day.startDate),
        startTime: new Date(day.startDate).toTimeString().slice(0, 5),
        endTime: new Date(day.endDate).toTimeString().slice(0, 5),
        guests: day.guests || 1,
        tableIds: (day.tableLinks || []).map((link) => link.tableId),
        tableSetups: (day.tableLinks || []).map((link) => ({
          tableId: link.tableId,
          setupId: link.setupId || "",
          usageTypeId: link.usageTypeId || link.usageType?.id || "",
          note: link.note || "",
        })),
        packageItems: [],
        serviceItems: [],
        note: day.note || "",
      })),
      rows: rows.length ? rows : [createEmptyRow()],
      sendEmail: false,
      includeLogo: false,
      emailMessage: "",
      attachments: Array.isArray(event.attachments) ? event.attachments : [],
      newAttachments: [],
    });
    setDialogOpen(true);
  };

  const updateFormField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "practiceType") {
        next.status = value === "PREVENTIVO" ? "PREVENTIVO" : "TO_CONFIRM";
      }
      return next;
    });
  };

  const updateDay = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      days: prev.days.map((day, idx) => {
        if (idx !== index) return day;
        if (field === "tableIds") {
          const tableIds = value;
          return {
            ...day,
            tableIds,
            tableSetups: (day.tableSetups || []).filter((item) => tableIds.includes(item.tableId)),
          };
        }
        return { ...day, [field]: value };
      }),
    }));
  };

  const updateDayTableSetupMeta = (dayIndex, tableId, patch) => {
    setForm((prev) => ({
      ...prev,
      days: prev.days.map((day, idx) => {
        if (idx !== dayIndex) return day;
        const others = (day.tableSetups || []).filter((item) => item.tableId !== tableId);
        const existing = (day.tableSetups || []).find((item) => item.tableId === tableId);
        const next = { ...(existing || { tableId }), ...patch, tableId };
        const hasSetup = Number(next.setupId) > 0;
        const hasUsageType = Number(next.usageTypeId) > 0;
        const hasNote = typeof next.note === "string" && next.note.trim();
        return {
          ...day,
          tableSetups: hasSetup || hasUsageType || hasNote ? [...others, next] : others,
        };
      }),
    }));
  };

  const updateDayTableSetup = (dayIndex, tableId, setupId) => {
    updateDayTableSetupMeta(dayIndex, tableId, { setupId: setupId ? Number(setupId) : "" });
  };

  const updateDayTableUsageType = (dayIndex, tableId, usageTypeId) => {
    updateDayTableSetupMeta(dayIndex, tableId, { usageTypeId: usageTypeId ? Number(usageTypeId) : "" });
  };

  const updateDayTableSetupNote = (dayIndex, tableId, note) => {
    updateDayTableSetupMeta(dayIndex, tableId, { note });
  };

  const updateRow = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      rows: prev.rows.map((row, idx) => (idx === index ? { ...row, ...patch } : row)),
    }));
  };

  const addDay = () => setForm((prev) => ({ ...prev, days: [...prev.days, { ...EMPTY_DAY }] }));
  const removeDay = (index) => setForm((prev) => ({ ...prev, days: prev.days.filter((_, idx) => idx !== index) }));
  const addRow = () => setForm((prev) => ({ ...prev, rows: [...prev.rows, createEmptyRow()] }));
  const removeRow = (index) => setForm((prev) => ({ ...prev, rows: prev.rows.filter((_, idx) => idx !== index) }));
  const duplicateRow = (index) => {
    setForm((prev) => {
      const row = prev.rows[index];
      if (!row) return prev;
      const duplicatedRow = { ...row };
      return {
        ...prev,
        rows: [
          ...prev.rows.slice(0, index + 1),
          duplicatedRow,
          ...prev.rows.slice(index + 1),
        ],
      };
    });
  };

  const serviceRowAppliesToDay = (row, dayIndex) =>
    row?.dayIndex === "all" || Number(row?.dayIndex) === Number(dayIndex);

  const maximumGuestsForServiceRow = useCallback((row) => {
    if (row?.type !== "service") return null;
    const guests = (form.days || [])
      .map((day, dayIndex) => serviceRowAppliesToDay(row, dayIndex) ? Number(day.guests || 0) : null)
      .filter((value) => Number.isFinite(value) && value > 0);
    return guests.length ? Math.min(...guests) : null;
  }, [form.days]);

  const serviceQuantityOverages = useMemo(() => (form.rows || [])
    .map((row, rowIndex) => {
      if (row.type !== "service") return null;
      const maxGuests = maximumGuestsForServiceRow(row);
      const quantity = Number(row.quantity || 0);
      if (maxGuests === null || quantity <= maxGuests) return null;
      return {
        rowIndex,
        quantity,
        maxGuests,
        description: row.description || `Servizio ${rowIndex + 1}`,
      };
    })
    .filter(Boolean), [form.rows, maximumGuestsForServiceRow]);

  const serviceAvailabilityForProductDate = useCallback((productId, date) => {
    const rows = serviceAvailabilityByDate[date] || [];
    return rows.find((item) => Number(item.productId) === Number(productId)) || null;
  }, [serviceAvailabilityByDate]);

  const requestedServiceQuantityForDate = useCallback((productId, date, dayIndex) => {
    return (form.rows || []).reduce((sum, item) => {
      if (item.type !== "service" || Number(item.refId) !== Number(productId)) return sum;
      if (!serviceRowAppliesToDay(item, dayIndex)) return sum;
      return sum + Number(item.quantity || 0);
    }, 0);
  }, [form.rows]);

  const getServiceAvailabilitySummary = useCallback((row) => {
    if (row.type !== "service" || !Number(row.refId)) return null;
    const dayEntries = (form.days || [])
      .map((day, dayIndex) => ({ day, dayIndex }))
      .filter(({ day, dayIndex }) => day.date && serviceRowAppliesToDay(row, dayIndex))
      .map(({ day, dayIndex }) => {
        const availability = serviceAvailabilityForProductDate(row.refId, day.date);
        if (!availability) return null;
        const remaining = Number(availability.remainingQuantity || 0);
        const requested = requestedServiceQuantityForDate(row.refId, day.date, dayIndex);
        const after = remaining - requested;
        return { date: day.date, remaining, requested, after };
      })
      .filter(Boolean);

    if (!dayEntries.length) return null;
    const hasConflict = dayEntries.some((item) => item.after < 0);
    if (dayEntries.length === 1) {
      const item = dayEntries[0];
      const exceededBy = Math.abs(Math.min(0, item.after));
      return {
        hasConflict,
        label: hasConflict
          ? `Non disponibile: richiesti ${item.requested}, disponibili ${item.remaining}`
          : `Massima disponibilit\u00e0 residua: ${item.after}`,
        tooltip: hasConflict
          ? `Riduci la quantità di ${exceededBy}`
          : `Disponibilità iniziale ${item.remaining}, quantità richiesta ${item.requested}`,
      };
    }

    const minAfter = Math.min(...dayEntries.map((item) => item.after));
    const firstConflict = dayEntries.find((item) => item.after < 0);
    return {
      hasConflict,
      label: hasConflict
        ? `Non disponibile in almeno una giornata`
        : `Massima disponibilit\u00e0 residua: ${minAfter}`,
      tooltip: dayEntries
        .map((item) => `${item.date}: disp. ${item.remaining}, richieste ${item.requested}, residuo ${item.after}`)
        .join("\n") || (firstConflict ? `Riduci la quantità per ${firstConflict.date}` : ""),
    };
  }, [form.days, requestedServiceQuantityForDate, serviceAvailabilityForProductDate]);

  const correspondenceEmails = useMemo(() => {
    const emails = [];
    if (isEmail(form.quoteEmail)) emails.push(form.quoteEmail.trim());
    if (form.clientEmailActive && isEmail(form.client?.email)) emails.push(form.client.email.trim());
    if (form.companyEmailActive && isEmail(form.company?.email)) emails.push(form.company.email.trim());
    return [...new Set(emails.map((email) => email.toLowerCase()))];
  }, [form.quoteEmail, form.clientEmailActive, form.client, form.companyEmailActive, form.company]);

  const validateForm = () => {
    if (!form.eventName.trim()) return t("congressReservations.validation.eventNameRequired");
    if (form.practiceType === "PREVENTIVO" && correspondenceEmails.length === 0) {
      return t("congressReservations.validation.quoteEmailRequired", {
        defaultValue: "For a quote, add at least one valid email: free recipient, active client, or active company.",
      });
    }
    if (form.sendEmail && correspondenceEmails.length === 0) {
      return t("congressReservations.validation.sendEmailRequired", {
        defaultValue: "To send the email, activate at least one valid recipient email.",
      });
    }
    if (!form.days.length) return t("congressReservations.validation.dayRequired");
    for (const day of form.days) {
      if (!day.date || !day.startTime || !day.endTime) return t("congressReservations.validation.dayDateTimeRequired");
      const start = toDateTimeIso(day.date, day.startTime);
      const end = toDateTimeIso(day.date, day.endTime);
      if (!start || !end || new Date(end) <= new Date(start)) return t("congressReservations.validation.dayEndAfterStart");
      if (Number(day.guests) <= 0) return t("congressReservations.validation.guestsPositive");
      if (form.status !== "PREVENTIVO" && (!Array.isArray(day.tableIds) || day.tableIds.length === 0)) {
        return isRestaurantTenant
          ? "Per salvare o confermare la prenotazione assegna almeno un tavolo a ogni giornata."
          : t("congressReservations.validation.roomRequiredForReservation");
      }
    }
    for (const [rowIndex, row] of form.rows.entries()) {
      if (!["package", "service"].includes(row.type) || !row.orario?.trim()) continue;
      if (!isTimeInsideRange(row.orario, "00:00", "23:59")) {
        return t("congressReservations.validation.serviceTimeInvalid", {
          defaultValue: "Service time must use HH:mm.",
        });
      }
      const targetIndexes = targetDayIndexesForRow(row, form.days.length);
      for (const dayIndex of targetIndexes) {
        const day = form.days[dayIndex];
        if (day && !isTimeInsideRange(row.orario, day.startTime, day.endTime)) {
          return t("congressReservations.validation.serviceTimeOutsideRoom", {
            defaultValue: "Service time {{time}} on row {{row}} must stay within the room occupation time.",
            time: row.orario,
            row: rowIndex + 1,
          });
        }
      }
    }
    const rowsMissingDepartment = form.rows.some((row) => {
      const isActiveServiceRow = row.type === "service" && Number(row.refId) > 0;
      return isActiveServiceRow && rowMatchesDepartmentScope(row) && !Number(row.departmentId);
    });
    if (rowsMissingDepartment) return t("congressReservations.validation.departmentRequired");
    return "";
  };

  const buildPayload = (forced = {}) => {
    return {
      practiceType: forced.practiceType || form.practiceType,
      eventName: form.eventName.trim(),
      status: forced.status || form.status,
      note: form.note.trim() || undefined,
      clientId: form.client?.id ?? null,
      companyId: form.company?.id ?? null,
      genericRecipientName: form.genericRecipientName.trim() || undefined,
      genericRecipientVatNumber: form.genericRecipientVatNumber.trim() || undefined,
      quoteEmail: form.quoteEmail.trim() || undefined,
      clientEmailActive: Boolean(form.clientEmailActive),
      companyEmailActive: Boolean(form.companyEmailActive),
      allowServiceQuantityAboveGuests: Boolean(forced.allowServiceQuantityAboveGuests),
      quoteValidUntil: form.quoteValidUntil ? new Date(`${form.quoteValidUntil}T23:59`).toISOString() : undefined,
      cancellationReason: (forced.cancellationReason ?? form.cancellationReason)?.trim() || undefined,
      days: form.days.map((day, dayIndex) => {
        const packageRows = form.rows.filter((row) =>
          row.type === "package" &&
          Number(row.refId) > 0 &&
          (row.dayIndex === "all" || Number(row.dayIndex) === dayIndex),
        );
        const serviceRows = form.rows.filter((row) =>
          row.type === "service" &&
          Number(row.refId) > 0 &&
          (row.dayIndex === "all" || Number(row.dayIndex) === dayIndex),
        );
        const customRows = form.rows.filter((row) =>
          row.type === "custom" &&
          String(row.description || "").trim() &&
          (row.dayIndex === "all" || Number(row.dayIndex) === dayIndex),
        );
        return ({
          dayDate: toDateTimeIso(day.date, day.startTime),
          startDate: toDateTimeIso(day.date, day.startTime),
          endDate: toDateTimeIso(day.date, day.endTime),
          guests: Number(day.guests || 1),
          note: day.note?.trim() || undefined,
          tableIds: (day.tableIds || []).map(Number),
          tableSetups: (day.tableSetups || []).map((item) => ({
            tableId: Number(item.tableId),
            setupId: Number(item.setupId) || undefined,
            usageTypeId: Number(item.usageTypeId) || undefined,
            note: item.note?.trim() || undefined,
          })),
          packageItems: packageRows.map((row) => ({
            packageId: Number(row.refId),
            departmentId: Number(row.departmentId) || undefined,
            description: row.description?.trim() || undefined,
            orario: row.orario?.trim() || undefined,
            quantity: Number(row.quantity || 1),
            unitPrice: Number(row.unitPrice || 0),
            note: row.note?.trim() || undefined,
          })),
          serviceItems: serviceRows.map((row) => ({
            productId: Number(row.refId),
            departmentId: Number(row.departmentId) || undefined,
            description: row.description?.trim() || undefined,
            orario: row.orario?.trim() || undefined,
            quantity: Number(row.quantity || 1),
            unitPrice: Number(row.unitPrice || 0),
            note: row.note?.trim() || undefined,
          })),
          customItems: customRows.map((row) => ({
            description: row.description,
            departmentId: Number(row.departmentId) || undefined,
            quantity: Number(row.quantity || 1),
            unitPrice: Number(row.unitPrice || 0),
            note: row.note?.trim() || undefined,
          })),
        });
      }),
    };
  };

  const practiceEmailSubject = () => {
    const name = form.eventName.trim();
    if (form.practiceType === "PREVENTIVO" || form.status === "PREVENTIVO") return t("congressReservations.emailSubject.quote", { name });
    if (form.practiceType === "PRENOTAZIONE") {
      return form.status === "CONFIRMED"
        ? t("congressReservations.emailSubject.confirmed", { name })
        : t("congressReservations.emailSubject.option", { name });
    }
    return t("congressReservations.emailSubject.summary", { name });
  };

  const practiceEmailUnit = (row) => {
    if (row.type === "package") return t("reports.package");
    if (row.type === "service") {
      const department = departments.find((item) => item.id === Number(row.departmentId));
      return department?.name ? t("congressReservations.rowCategory.serviceDepartment", { department: department.name }) : t("reports.service");
    }
    const department = departments.find((item) => item.id === Number(row.departmentId));
    return department?.name ? t("congressReservations.rowCategory.extraDepartment", { department: department.name }) : t("congressReservations.rowCategory.extra");
  };

  const selectPracticeClient = (client) => {
    setForm((prev) => ({
      ...prev,
      client,
      clientEmailActive: client ? Boolean(client.email) : false,
    }));
  };

  const selectPracticeCompany = (company) => {
    setForm((prev) => ({
      ...prev,
      company,
      companyEmailActive: company ? Boolean(company.email) : false,
    }));
  };

  const practiceEmailRows = form.rows
    .filter((row) => row.description || Number(row.refId) > 0)
    .map((row) => ({
      name: row.description || catalogRows.find((item) => item.type === row.type && item.refId === row.refId)?.description || t("congressReservations.quoteRow"),
      unit: row.type !== "custom" && row.orario?.trim()
        ? `${practiceEmailUnit(row)} ${row.orario.trim()}`
        : practiceEmailUnit(row),
      qty: Number(row.quantity || 1),
      unitPrice: Number(row.unitPrice || 0).toFixed(2),
      total: (Number(row.quantity || 1) * Number(row.unitPrice || 0)).toFixed(2),
    }));
  const practiceEmailTotal = practiceEmailRows.reduce((sum, row) => sum + Number(row.total || 0), 0);
  const practiceRecipientName = form.company?.companyName
    || `${form.client?.name || ""} ${form.client?.surname || ""}`.trim()
    || form.genericRecipientName
    || form.quoteEmail
    || t("client");
  const practiceEventDate = form.days
    .filter((day) => day.date)
    .map((day) => `${day.date}${day.startTime ? ` ${day.startTime}${day.endTime ? `-${day.endTime}` : ""}` : ""}`)
    .join(", ");
  const practiceRoomDetails = form.days.flatMap((day) => (day.tableIds || []).map((tableId) => {
    const selectedTable = tables.find((item) => Number(item.id) === Number(tableId));
    const metadata = (day.tableSetups || []).find((item) => Number(item.tableId) === Number(tableId));
    const setup = setups.find((item) => Number(item.id) === Number(metadata?.setupId));
    const usageType = usageTypes.find((item) => Number(item.id) === Number(metadata?.usageTypeId));
    return {
      roomName: selectedTable?.name || `Sala ${tableId}`,
      setupName: setup?.name || "",
      usageTypeName: usageType?.name || "",
    };
  }));
  const practiceEmailPreviewHtml = buildQuoteEmailHtml({
    clientName: practiceRecipientName,
    message: form.emailMessage,
    eventDate: practiceEventDate,
    validUntil: (form.practiceType === "PREVENTIVO" || form.status === "PREVENTIVO") ? form.quoteValidUntil : "",
    items: practiceEmailRows,
    total: practiceEmailTotal.toFixed(2),
    logoSrc: form.includeLogo ? tenantLogoBase64 : "",
    brandLabel: "Sale Congressi",
    language,
    templateKind: form.practiceType === "PRENOTAZIONE" ? "confirmation" : "quote",
    tenantType: "SALE_CONGRESSI",
    roomDetails: practiceRoomDetails,
  });

  const togglePracticeEmail = () => {
    if (!form.sendEmail) {
      const validation = validateForm();
      if (validation) {
        showSnackbar(validation, "error");
        return;
      }
    }
    setForm((prev) => {
      const sendEmail = !prev.sendEmail;
      return {
        ...prev,
        sendEmail,
        emailMessage: sendEmail
          && !prev.emailMessage.trim()
          ? (
            prev.practiceType === "PREVENTIVO" || prev.status === "PREVENTIVO"
              ? t("congressReservations.defaultQuoteEmailMessage")
              : prev.practiceType === "PRENOTAZIONE"
                ? t("congressReservations.defaultConfirmationEmailMessage")
                : prev.emailMessage
          )
          : prev.emailMessage,
      };
    });
  };

  const sendPracticeEmail = async (event) => {
    await inviaPreventivoEmail({
      to: correspondenceEmails.join(","),
      clientName: eventRecipient(event),
      subject: practiceEmailSubject(),
      message: form.emailMessage || "",
      emailType: form.practiceType === "PRENOTAZIONE" ? "CONFIRMATION" : "QUOTE",
      eventId: event.id,
      eventDate: practiceEventDate,
      validUntil: (form.practiceType === "PREVENTIVO" || form.status === "PREVENTIVO") ? form.quoteValidUntil || "" : "",
      attachments: (event.attachments || []).map((item) => ({
        id: item.id,
        name: item.description || item.originalName || t("congressReservations.attachment"),
        url: `/${item.filePath}`,
      })),
      items: practiceEmailRows,
      total: practiceEmailTotal.toFixed(2),
      includeLogo: Boolean(form.includeLogo),
      language: String(language || "it").toLowerCase().split(/[-_]/)[0],
    }, token, tenant);
    if (form.practiceType === "PREVENTIVO" || form.status === "PREVENTIVO") {
      return markReservationEventQuoteSent(event.id, token, tenant);
    }
    return event;
  };

  const splitRecipientName = (value) => {
    const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) return { name: parts[0] || "", surname: "-" };
    return { name: parts.slice(0, -1).join(" "), surname: parts[parts.length - 1] };
  };

  const handleCreateGenericClient = async () => {
    const genericName = form.genericRecipientName.trim();
    if (!genericName) {
      showSnackbar(t("congressReservations.genericClientNameRequired"), "warning");
      return;
    }
    setLoading(true);
    try {
      const { name, surname } = splitRecipientName(genericName);
      const clientPayload = {
        name,
        surname,
      };
      if (isEmail(form.quoteEmail)) clientPayload.email = form.quoteEmail.trim();
      if (form.genericRecipientVatNumber.trim()) clientPayload.vatNumber = form.genericRecipientVatNumber.trim();

      const client = await creaCliente(clientPayload, token, tenant);
      setClients((prev) => [client, ...prev.filter((item) => item.id !== client.id)]);
      setForm((prev) => ({ ...prev, client, clientEmailActive: Boolean(client.email) }));
      showSnackbar(t("congressReservations.genericClientCreated"), "success");
    } catch (err) {
      showSnackbar(italianErrorMessage(err, t("congressReservations.genericClientCreateError")), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGenericCompany = async () => {
    const companyName = form.genericRecipientName.trim();
    if (!companyName) {
      showSnackbar(t("congressReservations.genericCompanyNameRequired"), "warning");
      return;
    }
    setLoading(true);
    try {
      const companyPayload = {
        companyName,
      };
      if (form.genericRecipientVatNumber.trim()) companyPayload.vatNumber = form.genericRecipientVatNumber.trim();
      if (isEmail(form.quoteEmail)) companyPayload.email = form.quoteEmail.trim();

      const company = await createCompany(companyPayload, token, tenant);
      setCompanies((prev) => [company, ...prev.filter((item) => item.id !== company.id)]);
      setForm((prev) => ({ ...prev, company, companyEmailActive: Boolean(company.email) }));
      showSnackbar(t("congressReservations.genericCompanyCreated"), "success");
    } catch (err) {
      showSnackbar(italianErrorMessage(err, t("congressReservations.genericCompanyCreateError")), "error");
    } finally {
      setLoading(false);
    }
  };

  const persistPractice = async (forced = {}) => {
    setSavingPractice(true);
    setLoading(true);
    await new Promise((resolve) => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
    });
    try {
      const payload = buildPayload(forced);
      let saved = editingEvent
        ? await updateReservationEvent(editingEvent.id, payload, token, tenant)
        : await createReservationEvent(payload, token, tenant);

      const newAttachments = Array.isArray(form.newAttachments) ? form.newAttachments : [];
      if (newAttachments.length > 0) {
        for (const attachment of newAttachments) {
          saved = await uploadReservationEventAttachment(saved.id, attachment.file, attachment.description, token, tenant);
        }
      }

      let mailWarning = "";
      if (form.sendEmail) {
        try {
          saved = await sendPracticeEmail(saved);
        } catch (err) {
          mailWarning = t("congressReservations.mailWarning", {
            error: italianErrorMessage(err, t("congressReservations.sendError")),
          });
        }
      }

      await loadData();
      setDialogOpen(false);
      resetForm();
      showSnackbar(
        mailWarning ? t("congressReservations.practiceSavedWithWarning", { warning: mailWarning }) : t("congressReservations.practiceSaved"),
        mailWarning ? "warning" : "success"
      );
      if (saved.status === "NO_SHOW") {
        handleCheckout(saved, { preserveStatus: "NO_SHOW" });
      }
    } catch (err) {
      showSnackbar(italianErrorMessage(err, t("congressReservations.practiceSaveError")), "error");
    } finally {
      setLoading(false);
      setSavingPractice(false);
    }
  };

  const handleSave = async () => {
    if (savingPractice) return;

    const validation = validateForm();
    if (validation) {
      showSnackbar(validation, "error");
      return;
    }

    let allowServiceQuantityAboveGuests = false;
    if (serviceQuantityOverages.length > 0) {
      const details = serviceQuantityOverages
        .map((item) => `${item.description}: ${item.quantity} (ospiti ${item.maxGuests})`)
        .join("\n");
      allowServiceQuantityAboveGuests = await openPracticeConfirmDialog({
        title: t("congressReservations.serviceQuantityOverageTitle", { defaultValue: "Quantità servizi superiore agli ospiti" }),
        message: t("congressReservations.serviceQuantityOverageConfirm", {
          defaultValue: "Alcuni servizi superano il numero degli ospiti della sala:\n{{details}}\n\nVuoi salvare comunque?",
          details,
        }),
        confirmLabel: t("congressReservations.saveAnyway", { defaultValue: "Salva comunque" }),
        confirmColor: "warning",
      });
      if (!allowServiceQuantityAboveGuests) return;
    }

    if (form.status === "CANCELLED" && !String(form.cancellationReason || "").trim()) {
      setCancelReasonError("");
      setCancelReasonOpen(true);
      return;
    }

    if (editingEvent?.id && editingEvent.status !== "CANCELLED" && form.status === "CANCELLED") {
      const totals = await getLoadedPrepaymentTotals(editingEvent);
      const shouldContinue = totals.totalPrepayments <= 0 || await openPracticeConfirmDialog({
        title: t("congressReservations.cancelPracticeWarningTitle"),
        message: t("congressReservations.cancelConfirmWithPrepayments", {
          name: editingEvent.eventName,
          amount: formatMoney(totals.totalPrepayments),
        }),
        confirmLabel: t("confermaanullamento"),
        confirmColor: "error",
      });
      if (!shouldContinue) {
        return;
      }
    }

    await persistPractice({ allowServiceQuantityAboveGuests });
  };

  const confirmOutstandingPrepaymentsBeforeCancellation = async (event) => {
    if (!event?.id || event.status === "CANCELLED" || form.status !== "CANCELLED") return true;
    const totals = await getLoadedPrepaymentTotals(event);
    if (totals.totalPrepayments <= 0) return true;
    return openPracticeConfirmDialog({
      title: t("congressReservations.cancelPracticeWarningTitle"),
      message: t("congressReservations.cancelConfirmWithPrepayments", {
        name: event.eventName,
        amount: formatMoney(totals.totalPrepayments),
      }),
      confirmLabel: t("confermaanullamento"),
      confirmColor: "error",
    });
  };

  const submitCancellationReason = async () => {
    const reason = String(form.cancellationReason || "").trim();
    if (!reason) {
      setCancelReasonError(t("congressReservations.cancellationReasonRequired"));
      return;
    }
    setCancelReasonOpen(false);
    setCancelReasonError("");
    if (!await confirmOutstandingPrepaymentsBeforeCancellation(editingEvent)) return;
    await persistPractice({
      cancellationReason: reason,
      allowServiceQuantityAboveGuests: serviceQuantityOverages.length > 0,
    });
  };

  const handleConvertToReservation = async (event) => {
    const requiresTables = (event?.days || []).some((day) => !Array.isArray(day?.tableLinks) || day.tableLinks.length === 0);
    openEditEvent(event);
    setForm((prev) => ({
      ...prev,
      practiceType: "PRENOTAZIONE",
      status: "TO_CONFIRM",
    }));
    if (requiresTables) {
      showSnackbar(
        isRestaurantTenant
          ? "Per convertire il preventivo assegna almeno un tavolo a ogni giornata."
          : t("congressReservations.validation.roomRequiredForReservation"),
        "warning"
      );
    }
  };

  const handleConfirm = async (event) => {
    const requiresTables = (event?.days || []).some((day) => !Array.isArray(day?.tableLinks) || day.tableLinks.length === 0);
    if (requiresTables) {
      openEditEvent(event);
      setForm((prev) => ({
        ...prev,
        practiceType: "PRENOTAZIONE",
        status: "CONFIRMED",
      }));
      showSnackbar(
        isRestaurantTenant
          ? "Prima di confermare assegna almeno un tavolo a ogni giornata."
          : t("congressReservations.validation.roomRequiredForReservation"),
        "warning"
      );
      return;
    }
    try {
      await updateReservationEvent(event.id, {
        ...buildEventPayloadFromEvent(event),
        practiceType: "PRENOTAZIONE",
        status: "CONFIRMED",
      }, token, tenant);
      await loadData();
      showSnackbar(t("congressReservations.reservationConfirmed"), "success");
    } catch (err) {
      showSnackbar(italianErrorMessage(err, t("congressReservations.reservationConfirmError")), "error");
    }
  };

  const handleCheckIn = async (event) => {
    if (!isEventArrivalToday(event?.days)) {
      showSnackbar(t("congressReservations.checkInArrivalDateOnly", {
        defaultValue: "Il check-in è consentito solo nella data di arrivo del congresso.",
      }), "warning");
      return;
    }
    try {
      await patchReservationEventStatus(event.id, "CHECKED_IN", token, tenant);
      await loadData();
      showSnackbar(t("congressReservations.checkInSuccess", { defaultValue: "Check-in impostato" }), "success");
    } catch (err) {
      showSnackbar(italianErrorMessage(
        err,
        t("congressReservations.checkInError", { defaultValue: "Errore durante il check-in" }),
      ), "error");
    }
  };

  const handleRestoreCheckIn = async (event) => {
    const shouldRestore = await openPracticeConfirmDialog({
      title: t("congressReservations.restoreCheckInTitle"),
      message: t("congressReservations.restoreCheckInConfirm", { name: event.eventName }),
      helpText: t("congressReservations.restoreCheckInHelp"),
      confirmLabel: t("congressReservations.restoreCheckIn"),
      confirmColor: "warning",
    });
    if (!shouldRestore) return;

    try {
      await patchReservationEventStatus(event.id, "CONFIRMED", token, tenant);
      await loadData();
      showSnackbar(t("congressReservations.restoreCheckInSuccess"), "success");
    } catch (err) {
      showSnackbar(italianErrorMessage(err, t("congressReservations.restoreCheckInError")), "error");
    }
  };

  const buildEventPayloadFromEvent = (event) => ({
    practiceType: event.practiceType || "PRENOTAZIONE",
    eventName: event.eventName,
    status: event.status,
    note: event.note || undefined,
    clientId: event.clientId || undefined,
    companyId: event.companyId || undefined,
    quoteEmail: event.quoteEmail || undefined,
    quoteValidUntil: event.quoteValidUntil || undefined,
    cancellationReason: event.cancellationReason || undefined,
    days: (event.days || []).map((day) => ({
      dayDate: day.dayDate,
      startDate: day.startDate,
      endDate: day.endDate,
      guests: day.guests,
      note: day.note || undefined,
      tableIds: (day.tableLinks || []).map((link) => link.tableId),
      tableSetups: (day.tableLinks || []).map((link) => ({
        tableId: link.tableId,
        setupId: link.setupId,
        usageTypeId: link.usageTypeId || link.usageType?.id,
        note: link.note,
      })).filter((item) => item.setupId || item.usageTypeId || item.note),
      packageItems: (day.packages || []).map((item) => ({
        packageId: item.packageId,
        orario: item.orario,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        note: item.note,
      })),
      serviceItems: (day.services || []).map((item) => ({
        productId: item.productId,
        variantId: item.variantId,
        orario: item.orario,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        note: item.note,
      })),
      customItems: (day.customRows || []).map((item) => ({
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        note: item.note,
      })),
    })),
  });

  const handleDelete = async (event) => {
    if (event?.hasIssuedAccount) {
      showSnackbar(t("congressReservations.practiceDeleteBlockedByAccount", {
        defaultValue: "La pratica non può essere eliminata perché è già stato emesso un conto.",
      }), "warning");
      return;
    }
    const totals = await getLoadedPrepaymentTotals(event);

    const confirmKey = totals.totalPrepayments > 0
      ? "congressReservations.deleteConfirmWithPrepayments"
      : "congressReservations.deleteConfirm";
    const shouldDelete = await openPracticeConfirmDialog({
      title: t(totals.totalPrepayments > 0
        ? "congressReservations.deletePracticeWarningTitle"
        : "congressReservations.deletePracticeTitle"),
      message: t(confirmKey, {
        name: event.eventName,
        amount: formatMoney(totals.totalPrepayments),
      }),
      confirmLabel: t("elimina"),
      confirmColor: "error",
    });
    if (!shouldDelete) return;
    try {
      await deleteReservationEvent(event.id, token, tenant);
      await loadData();
      showSnackbar(t("congressReservations.practiceDeleted"), "success");
    } catch (err) {
      const message = String(err?.message || "");
      const hasLinkedPrepayments = message.includes("PrepaymentMovement_reservationEventId_fkey")
        || message.includes("PrepaymentMovement");
      showSnackbar(
        hasLinkedPrepayments
          ? t("congressReservations.practiceDeleteBlockedByPrepayments")
          : italianErrorMessage(err, t("congressReservations.practiceDeleteError")),
        "error"
      );
    }
  };

  const handleCheckout = (event, { preserveStatus = null } = {}) => {
    const eventId = Number(event?.id);
    const firstTableId = (event.days || [])
      .flatMap((day) => day.tableLinks || [])
      .map((link) => link.tableId)
      .find(Boolean);
    if (!firstTableId) {
      showSnackbar(t("congressReservations.assignRoomBeforeCheckout"), "warning");
      return;
    }
    const params = new URLSearchParams({ tableId: String(Number(firstTableId)) });
    if (Number.isInteger(eventId) && eventId > 0) {
      params.set("reservationEventId", String(eventId));
    }
    if (preserveStatus) params.set("preserveReservationEventStatus", preserveStatus);
    navigate(`/punto_cassa?${params.toString()}`, {
      state: {
        checkoutTableId: Number(firstTableId),
        ...(Number.isInteger(eventId) && eventId > 0 ? { reservationEventId: eventId } : {}),
        ...(preserveStatus ? { preserveReservationEventStatus: preserveStatus } : {}),
      },
    });
  };

  const loadPrepaymentSummary = useCallback(async (eventId) => {
    setLoadingPrepaymentSummary(true);
    try {
      const summary = await getReservationEventPrepaymentSummary(eventId, token, tenant);
      setPrepaymentTotalsByEventId((prev) => ({ ...prev, [eventId]: summary?.totals || {} }));
      setPrepaymentMovementsByEventId((prev) => ({
        ...prev,
        [eventId]: Array.isArray(summary?.movements) ? summary.movements : [],
      }));
      return summary;
    } catch (err) {
      showSnackbar(italianErrorMessage(err, t("congressReservations.prepayments.summaryLoadError")), "error");
      return null;
    } finally {
      setLoadingPrepaymentSummary(false);
    }
  }, [showSnackbar, t, tenant, token]);

  const getLoadedPrepaymentTotals = useCallback(async (event) => {
    let totals = getEventPrepaymentTotals(event, prepaymentTotalsByEventId);
    if (event?.id && !hasEventPrepaymentTotals(event, prepaymentTotalsByEventId)) {
      const summary = await loadPrepaymentSummary(event.id);
      if (summary?.totals) {
        totals = getEventPrepaymentTotals(
          { ...event, prepaymentTotals: summary.totals },
          { ...prepaymentTotalsByEventId, [event.id]: summary.totals }
        );
      }
    }
    return totals;
  }, [loadPrepaymentSummary, prepaymentTotalsByEventId]);

  const openPracticeConfirmDialog = useCallback((options) => new Promise((resolve) => {
    practiceConfirmResolverRef.current = resolve;
    setPracticeConfirmDialog(options);
  }), []);

  const closePracticeConfirmDialog = useCallback((confirmed) => {
    const resolver = practiceConfirmResolverRef.current;
    practiceConfirmResolverRef.current = null;
    setPracticeConfirmDialog(null);
    if (resolver) resolver(Boolean(confirmed));
  }, []);

  useEffect(() => {
    if (!dialogOpen || !editingEvent?.id) return;
    if (hasEventPrepaymentTotals(editingEvent, prepaymentTotalsByEventId)) return;
    loadPrepaymentSummary(editingEvent.id);
  }, [dialogOpen, editingEvent, loadPrepaymentSummary, prepaymentTotalsByEventId]);

  const openPrepaymentDialog = (event) => {
    setPrepaymentDialogEvent(event);
    setPrepaymentOperation("RECEIVE");
    setPrepaymentForm({
      type: "DEPOSIT",
      amount: "",
      vatRate: "10",
      paymentMethod: "CASH",
      documentType: "PLAIN_RECEIPT",
      invoiceId: "",
      vatNature: "",
      documentNumber: "",
      documentDate: toLocalDateInput(new Date()),
      description: t("congressReservations.prepayments.defaultDescription", { name: event?.eventName || "" }),
      note: "",
      sourceMovementId: "",
    });
    if (event?.id) {
      loadPrepaymentSummary(event.id);
    }
  };

  const closePrepaymentDialog = () => {
    if (savingPrepayment) return;
    setPrepaymentDialogEvent(null);
  };

  const updatePrepaymentForm = (field, value) => {
    setPrepaymentForm((prev) => ({ ...prev, [field]: value }));
  };

  const changePrepaymentType = (type) => {
    setPrepaymentForm((prev) => ({
      ...prev,
      type,
      documentType: type === "DEPOSIT" ? "PLAIN_RECEIPT" : "COMMERCIAL_DOCUMENT",
      vatRate: type === "DEPOSIT" ? "" : prev.vatRate || "10",
      invoiceId: "",
      vatNature: "",
      documentNumber: "",
      documentDate: toLocalDateInput(new Date()),
      sourceMovementId: "",
    }));
  };

  const changePrepaymentOperation = (operation) => {
    setPrepaymentOperation(operation);
    setPrepaymentForm((prev) => ({
      ...prev,
      amount: "",
      documentType: operation === "REFUND"
        ? "PLAIN_RECEIPT"
        : prev.type === "DEPOSIT"
          ? "PLAIN_RECEIPT"
          : "COMMERCIAL_DOCUMENT",
      invoiceId: "",
      vatNature: "",
      documentNumber: "",
      documentDate: toLocalDateInput(new Date()),
      sourceMovementId: "",
    }));
  };

  const prepaymentErrorMessage = (err) => {
    const details = err?.details || {};
    if (err?.code === "PREPAYMENTS_EXCEED_PRACTICE_TOTAL") {
      return t("congressReservations.prepayments.errors.exceedsTotal", {
        practiceTotal: formatMoney(details.practiceTotal),
        currentPrepayments: formatMoney(details.currentPrepayments),
        requestedAmount: formatMoney(details.requestedAmount),
      });
    }
    if (err?.code === "REFUND_EXCEEDS_AVAILABLE_AMOUNT") {
      return t("congressReservations.prepayments.errors.refundExceeds", {
        refundableAmount: formatMoney(details.refundableAmount),
        requestedAmount: formatMoney(details.requestedAmount),
      });
    }
    if (err?.code === "IDEMPOTENCY_KEY_REUSED") {
      return t("congressReservations.prepayments.errors.idempotencyKeyReused");
    }
    if (err?.code === "INVALID_REVERSAL_DOCUMENT") {
      return t("congressReservations.prepayments.errors.invalidReversalDocument", {
        documentType: prepaymentDocumentTypeLabel(details.expectedDocument, t),
      });
    }
    if (err?.code === "TD02_NOT_SENT") {
      return t("congressReservations.prepayments.errors.td02NotSent");
    }
    return italianErrorMessage(err, t("congressReservations.prepayments.errors.generic"));
  };

  const prepaymentIdempotencyKey = (eventId, operation) => (
    `reservation-event:${eventId}:prepayment:${operation}:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`
  );

  const mergePrepaymentResult = (eventId, result) => {
    if (result?.totals) {
      setPrepaymentTotalsByEventId((prev) => ({ ...prev, [eventId]: result.totals }));
    }
    if (result?.movement?.id) {
      setPrepaymentMovementsByEventId((prev) => {
        const existing = Array.isArray(prev[eventId]) ? prev[eventId] : [];
        const next = [result.movement, ...existing.filter((movement) => Number(movement.id) !== Number(result.movement.id))];
        return { ...prev, [eventId]: next };
      });
    }
  };

  const submitPrepayment = async () => {
    const event = prepaymentDialogEvent;
    if (!event?.id || savingPrepayment) return;
    const amount = Number(prepaymentForm.amount);
    const currentMovements = normalizePrepaymentMovements(event, prepaymentMovementsByEventId);
    const sourceMovementId = Number(prepaymentForm.sourceMovementId);
    const sourceMovement = currentMovements.find((movement) => Number(movement.id) === sourceMovementId);
    const isPlainReceiptRefund = prepaymentOperation === "REFUND" && sourceMovement?.documentType === "PLAIN_RECEIPT";
    if ((prepaymentOperation === "RECEIVE" || isPlainReceiptRefund) && (!Number.isFinite(amount) || amount <= 0)) {
      showSnackbar(t("congressReservations.prepayments.validation.validAmountRequired"), "warning");
      return;
    }

    setSavingPrepayment(true);
    try {
      let result;
      if (prepaymentOperation === "REFUND") {
        if (!Number.isInteger(sourceMovementId) || sourceMovementId <= 0) {
          showSnackbar(t("congressReservations.prepayments.validation.sourceRequired"), "warning");
          return;
        }
        if (!sourceMovement) {
          showSnackbar(t("congressReservations.prepayments.validation.sourceRequired"), "warning");
          return;
        }
        const sourceAvailableAmount = prepaymentMovementAvailableAmount(sourceMovement, currentMovements);
        if (isPlainReceiptRefund && amount > sourceAvailableAmount) {
          showSnackbar(t("congressReservations.prepayments.errors.refundExceeds", {
            refundableAmount: formatMoney(sourceAvailableAmount),
            requestedAmount: formatMoney(amount),
          }), "warning");
          return;
        }
        const allowedDocumentTypes = refundDocumentTypesForMovement(sourceMovement).map((item) => item.value);
        if (!allowedDocumentTypes.includes(prepaymentForm.documentType)) {
          showSnackbar(t("congressReservations.prepayments.validation.refundDocumentRequired"), "warning");
          return;
        }
        const requiresCommercialDocument = ["COMMERCIAL_CANCELLATION", "COMMERCIAL_RETURN"].includes(prepaymentForm.documentType);
        if (requiresCommercialDocument
          && (!prepaymentForm.documentNumber.trim() || !prepaymentForm.documentDate)) {
          showSnackbar(t("congressReservations.prepayments.validation.refundDocumentRequired"), "warning");
          return;
        }

        if (isPlainReceiptRefund) {
          result = await refundReservationEventPrepayment(event.id, sourceMovementId, {
            amount,
            paymentMethod: prepaymentForm.paymentMethod,
            documentType: "PLAIN_RECEIPT",
            note: prepaymentForm.note.trim() || undefined,
            idempotencyKey: prepaymentIdempotencyKey(event.id, "refund"),
          }, token, tenant);
        } else {
          const payload = {
            paymentMethod: prepaymentForm.paymentMethod,
            documentType: prepaymentForm.documentType,
            note: prepaymentForm.note.trim() || undefined,
            idempotencyKey: prepaymentIdempotencyKey(event.id, "reverse"),
          };
          if (requiresCommercialDocument) {
            payload.documentNumber = prepaymentForm.documentNumber.trim();
            payload.documentDate = new Date(`${prepaymentForm.documentDate}T12:00:00`).toISOString();
          }
          result = await reverseReservationEventPrepayment(event.id, sourceMovementId, payload, token, tenant);
        }
      } else {
        const payload = {
          type: prepaymentForm.type,
          amount,
          paymentMethod: prepaymentForm.paymentMethod,
          documentType: prepaymentForm.type === "DEPOSIT" ? "PLAIN_RECEIPT" : prepaymentForm.documentType,
          description: prepaymentForm.description.trim() || undefined,
          note: prepaymentForm.note.trim() || undefined,
          idempotencyKey: prepaymentIdempotencyKey(event.id, "receive"),
        };
        if (prepaymentForm.type === "ADVANCE") {
          const vatRate = Number(prepaymentForm.vatRate);
          if (!Number.isFinite(vatRate) || vatRate < 0 || vatRate > 100) {
            showSnackbar(t("congressReservations.prepayments.validation.vatRateRequired"), "warning");
            return;
          }
          if (!prepaymentForm.description.trim()) {
            showSnackbar(t("congressReservations.prepayments.validation.descriptionRequired"), "warning");
            return;
          }
          const requiresVatNature = prepaymentForm.documentType === "INVOICE_TD02" && vatRate === 0;
          if (requiresVatNature && !prepaymentForm.vatNature) {
            showSnackbar(t("congressReservations.prepayments.validation.vatNatureRequired"), "warning");
            return;
          }
          if (prepaymentForm.documentType === "COMMERCIAL_DOCUMENT"
            && (!prepaymentForm.documentNumber.trim() || !prepaymentForm.documentDate)) {
            showSnackbar(t("congressReservations.prepayments.validation.commercialDocumentRequired"), "warning");
            return;
          }
          payload.vatRate = Number(prepaymentForm.vatRate);
          if (requiresVatNature) {
            payload.vatNature = prepaymentForm.vatNature;
          }
          if (prepaymentForm.documentType === "COMMERCIAL_DOCUMENT") {
            payload.documentNumber = prepaymentForm.documentNumber.trim();
            payload.documentDate = new Date(`${prepaymentForm.documentDate}T12:00:00`).toISOString();
          }
        }
        result = await createReservationEventPrepayment(event.id, payload, token, tenant);
      }

      mergePrepaymentResult(event.id, result);
      await loadPrepaymentSummary(event.id);
      const invoiceDelivery = result?.invoiceDelivery;
      if (invoiceDelivery?.status) {
        showSnackbar(
          t(`congressReservations.prepayments.invoiceDelivery.${invoiceDelivery.status}`, {
            invoiceId: invoiceDelivery.invoiceId || "",
            message: invoiceDelivery.message || "",
          }),
          prepaymentDeliverySeverity(invoiceDelivery),
        );
      } else {
        showSnackbar(
          prepaymentOperation === "REFUND"
            ? (isPlainReceiptRefund
              ? t("congressReservations.prepayments.refundSuccess")
              : t("congressReservations.prepayments.reverseSuccess"))
            : t("congressReservations.prepayments.receivedSuccess"),
          "success",
        );
      }
      setPrepaymentForm((prev) => ({ ...prev, amount: "", note: "", sourceMovementId: "" }));
    } catch (err) {
      showSnackbar(prepaymentErrorMessage(err), err?.status === 409 ? "warning" : "error");
    } finally {
      setSavingPrepayment(false);
    }
  };

  const openSetupManager = (table) => {
    setSetupManagerTable(table);
    setSetupManagerSetupIds((table.compatibleSetups || []).map((item) => item.setupId));
  };

  const saveSetupManager = async () => {
    if (!setupManagerTable) return;
    try {
      await updateCompatibleSetups(setupManagerTable.id, setupManagerSetupIds, token, tenant);
      await loadData();
      setSetupManagerTable(null);
      showSnackbar(t("congressReservations.compatibilityUpdated"), "success");
    } catch (err) {
      showSnackbar(italianErrorMessage(err, t("congressReservations.compatibilityUpdateError")), "error");
    }
  };

  const handleDownloadSummaryPdf = async () => {
    try {
      const blob = await downloadReservationEventSummaryPdf({
        from: filters.from,
        to: filters.to,
        status: filters.status,
        tableId: filters.tableId,
        tableName: tables.find((table) => Number(table.id) === Number(filters.tableId))?.name || "",
        q: filters.recipient,
        categoryIds: filters.categoryIds,
        categoryNames: categoryOptions
          .filter((category) => filters.categoryIds.includes(category.id))
          .map((category) => category.name)
          .join(", "),
        hidePrices: filters.hidePrices,
      }, token, tenant);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "riepilogo-ordini-sale-congressi.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showSnackbar(italianErrorMessage(err, t("congressReservations.pdfError")), "error");
    }
  };

  const proformaRowsForDay = (day) => [
    ...(day.packages || []).map((item) => ({
      key: `package:${item.id}`,
      type: "package",
      id: item.id,
      label: item.description || item.package?.name || "Pacchetto",
      amount: Number(item.quantity || 0) * Number(item.unitPrice ?? item.package?.price ?? 0),
    })),
    ...(day.services || []).map((item) => ({
      key: `service:${item.id}`,
      type: "service",
      id: item.id,
      label: item.description || item.product?.name || "Servizio",
      amount: Number(item.quantity || 0) * Number(item.unitPrice ?? item.product?.price ?? 0),
    })),
    ...(day.customRows || []).map((item) => ({
      key: `custom:${item.id}`,
      type: "custom",
      id: item.id,
      label: item.description || "Extra",
      amount: Number(item.quantity || 0) * Number(item.unitPrice || 0),
    })),
  ];

  const openProformaDialog = (event) => {
    setProformaDialogEvent(event);
    setProformaSelectedDayIds((event.days || []).map((day) => Number(day.id)));
    setProformaSelectedRowKeys([]);
  };

  const closeProformaDialog = (force = false) => {
    if (proformaDownloading && !force) return;
    setProformaDialogEvent(null);
    setProformaSelectedDayIds([]);
    setProformaSelectedRowKeys([]);
  };

  const toggleProformaDay = (dayId) => {
    const numericId = Number(dayId);
    setProformaSelectedDayIds((prev) => (
      prev.includes(numericId)
        ? prev.filter((id) => id !== numericId)
        : [...prev, numericId]
    ));
  };

  const toggleProformaRow = (rowKey) => {
    setProformaSelectedRowKeys((prev) => (
      prev.includes(rowKey)
        ? prev.filter((key) => key !== rowKey)
        : [...prev, rowKey]
    ));
  };

  const handleDownloadProformaPdf = async () => {
    if (!proformaDialogEvent?.id) return;
    const rowSelections = proformaSelectedRowKeys.reduce((acc, key) => {
      const [type, id] = String(key).split(":");
      if (!acc[type]) acc[type] = [];
      const numericId = Number(id);
      if (Number.isInteger(numericId) && numericId > 0) acc[type].push(numericId);
      return acc;
    }, {});
    if (proformaSelectedDayIds.length === 0 && proformaSelectedRowKeys.length === 0) {
      showSnackbar("Seleziona almeno una giornata o un servizio", "warning");
      return;
    }
    setProformaDownloading(true);
    try {
      const blob = await downloadReservationEventProformaPdf(proformaDialogEvent.id, {
        dayIds: proformaSelectedDayIds,
        packageIds: rowSelections.package || [],
        serviceIds: rowSelections.service || [],
        customRowIds: rowSelections.custom || [],
      }, token, tenant);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = String(proformaDialogEvent.eventName || "evento")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      link.href = url;
      link.download = `conto-proforma-${safeName || "evento"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      closeProformaDialog(true);
    } catch (err) {
      showSnackbar(italianErrorMessage(err, "Errore durante la creazione del conto proforma"), "error");
    } finally {
      setProformaDownloading(false);
    }
  };

  const handleSendSummaryEmail = async () => {
    const draft = summaryRecipientDraft.trim();
    const recipients = [...summaryRecipients, ...(draft ? [draft] : [])];
    if (recipients.length === 0 || recipients.some((email) => !/.+@.+\..+/.test(email))) {
      showSnackbar(t("congressReservations.validation.sendEmailRequired"), "error");
      return;
    }
    try {
      await sendReservationEventSummaryEmail({
        recipients,
        filters: {
          from: filters.from,
          to: filters.to,
          status: filters.status,
          tableId: filters.tableId,
          tableName: tables.find((table) => Number(table.id) === Number(filters.tableId))?.name || "",
          q: filters.recipient,
          categoryIds: filters.categoryIds,
          categoryNames: categoryOptions
            .filter((category) => filters.categoryIds.includes(category.id))
            .map((category) => category.name)
            .join(", "),
          hidePrices: filters.hidePrices,
        },
      }, token, tenant);
      setSummaryEmailOpen(false);
      setSummaryRecipients([]);
      setSummaryRecipientDraft("");
      showSnackbar(t("congressReservations.summaryEmailSent"), "success");
    } catch (err) {
      showSnackbar(italianErrorMessage(err, t("congressReservations.summaryEmailError")), "error");
    }
  };

  const addSummaryRecipient = () => {
    const email = summaryRecipientDraft.trim();
    if (!/.+@.+\..+/.test(email)) {
      showSnackbar(t("congressReservations.validation.sendEmailRequired"), "error");
      return;
    }
    if (!summaryRecipients.some((item) => item.toLowerCase() === email.toLowerCase())) {
      setSummaryRecipients((prev) => [...prev, email]);
    }
    setSummaryRecipientDraft("");
  };

  const eventsForCell = (tableId, day) => {
    const dayKey = toLocalDateInput(day);
    return boardEvents
      .map((event) => ({ event, display: findEventDayForTableDay(event, tableId, day, tableById, true) }))
      .filter(({ display }) => Boolean(display) && toLocalDateInput(display.day.startDate) === dayKey)
      .sort((a, b) => new Date(a.display.day.startDate) - new Date(b.display.day.startDate));
  };

  const eventsForTimeSlot = (tableId, slotStart) => {
    return boardEvents
      .map((event) => {
        const display = findEventDayForTableDay(event, tableId, slotStart, tableById, true);
        return { event, day: display?.day, isRelatedBlock: Boolean(display?.isRelatedBlock), sourceTable: display?.sourceTable };
      })
      .filter(({ day }) => Boolean(day))
      .sort((a, b) => new Date(a.day.startDate) - new Date(b.day.startDate));
  };

  const dayEventGridPosition = (day) => {
    const dayStart = new Date(plannerDate);
    dayStart.setHours(DAY_START_HOUR, 0, 0, 0);
    const dayEnd = new Date(plannerDate);
    dayEnd.setHours(DAY_END_HOUR, 0, 0, 0);

    const start = new Date(day.startDate);
    const end = new Date(day.endDate);
    const visibleStart = start < dayStart ? dayStart : start;
    const visibleEnd = end > dayEnd ? dayEnd : end;
    if (visibleEnd <= dayStart || visibleStart >= dayEnd) return null;
    const startOffset = (visibleStart - dayStart) / (1000 * 60 * 60);
    const endOffset = (visibleEnd - dayStart) / (1000 * 60 * 60);
    const startIndex = Math.max(0, Math.min(dayTimeSlots.length - 1, Math.floor(startOffset)));
    const endIndex = Math.max(startIndex + 1, Math.min(dayTimeSlots.length, Math.ceil(endOffset)));

    return {
      startIndex,
      endIndex,
      gridColumn: `${startIndex + 2} / ${endIndex + 2}`,
    };
  };

  const layoutDayEvents = (items) => {
    const lanes = [];
    return items
      .map((item) => {
        const position = dayEventGridPosition(item.day);
        return position ? { ...item, position } : null;
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.position.startIndex !== b.position.startIndex) return a.position.startIndex - b.position.startIndex;
        return a.position.endIndex - b.position.endIndex;
      })
      .map((item) => {
        let laneIndex = lanes.findIndex((laneEnd) => laneEnd <= item.position.startIndex);
        if (laneIndex === -1) {
          laneIndex = lanes.length;
          lanes.push(item.position.endIndex);
        } else {
          lanes[laneIndex] = item.position.endIndex;
        }
        return {
          ...item,
          laneIndex,
          laneCount: lanes.length,
        };
      });
  };

  const agendaRows = useMemo(() => {
    return boardEvents
      .flatMap((event) => (event.days || []).map((day) => ({ event, day })))
      .filter(({ day }) => toLocalDateInput(day.startDate) === toLocalDateInput(plannerDate))
      .sort((a, b) => new Date(a.day.startDate) - new Date(b.day.startDate));
  }, [boardEvents, plannerDate]);

  const getEffectiveEventStatus = (event) => {
    if (!event) return "";
    if (event.status === "COMPLETED") return "COMPLETED";
    if (event.status === "PREVENTIVO") {
      const validUntil = event.quoteValidUntil ? new Date(event.quoteValidUntil).getTime() : null;
      if (Number.isFinite(validUntil) && validUntil < nowTick.getTime()) return "EXPIRED_QUOTE";
      return "PREVENTIVO";
    }
    if (["CANCELLED", "NO_SHOW"].includes(event.status)) return event.status;
    return event.status;
  };

  const statusColor = (status) => {
    if (status === "PREVENTIVO") return "warning";
    if (status === "EXPIRED_QUOTE") return "error";
    if (status === "CHECKED_IN") return "warning";
    if (status === "CONFIRMED") return "success";
    if (status === "TO_CONFIRM") return "info";
    if (status === "COMPLETED") return "success";
    if (status === "CANCELLED" || status === "NO_SHOW") return "error";
    return "default";
  };

  const renderStatusChip = (event) => {
    const effectiveStatus = getEffectiveEventStatus(event);
    const confirmedColor = eventColors.confirmed || "#2E7D32";
    return (
      <Chip
        size="small"
        color={effectiveStatus === "CONFIRMED" ? "default" : statusColor(effectiveStatus)}
        label={t(STATUS_LABEL_KEYS[effectiveStatus] || effectiveStatus)}
        sx={effectiveStatus === "CONFIRMED" ? {
          bgcolor: confirmedColor,
          color: theme.palette.getContrastText(confirmedColor),
          fontWeight: 700,
        } : undefined}
      />
    );
  };

  const getEventCardStyles = (event, isRelatedBlock = false) => {
    if (isRelatedBlock) {
      return {
        bgcolor: relatedBg,
        border: `1px dashed ${relatedBorder}`,
        boxShadow: "none",
        opacity: 0.86,
      };
    }
    const effectiveStatus = getEffectiveEventStatus(event);
    if (effectiveStatus === "CHECKED_IN") {
      return {
        bgcolor: alpha(eventColors.inProgress || "#F59E0B", theme.palette.mode === "dark" ? 0.24 : 0.18),
        border: `1px solid ${alpha(eventColors.inProgress || "#F59E0B", 0.65)}`,
        boxShadow: eventShadow,
        opacity: 1,
      };
    }
    if (effectiveStatus === "EXPIRED_QUOTE") {
      return {
        bgcolor: alpha(eventColors.expiredQuote || "#DC2626", theme.palette.mode === "dark" ? 0.24 : 0.14),
        border: `1px solid ${alpha(eventColors.expiredQuote || "#DC2626", 0.65)}`,
        boxShadow: eventShadow,
        opacity: 1,
      };
    }
    if (effectiveStatus === "COMPLETED") {
      return {
        bgcolor: alpha(eventColors.completed || "#16A34A", theme.palette.mode === "dark" ? 0.24 : 0.16),
        border: `1px solid ${alpha(eventColors.completed || "#16A34A", 0.62)}`,
        boxShadow: "none",
        opacity: 1,
      };
    }
    if (event.status === "PREVENTIVO") {
      return {
        bgcolor: preventivoBg,
        border: `1px solid ${preventivoBorder}`,
        boxShadow: eventShadow,
        opacity: 1,
      };
    }
    if (event.status === "CONFIRMED") {
      const confirmedColor = eventColors.confirmed || "#2E7D32";
      return {
        bgcolor: alpha(confirmedColor, theme.palette.mode === "dark" ? 0.24 : 0.16),
        border: `1px solid ${alpha(confirmedColor, 0.62)}`,
        boxShadow: "none",
        opacity: 1,
      };
    }
    return {
      bgcolor: infoBg,
      border: `1px solid ${infoBorder}`,
      boxShadow: "none",
      opacity: 1,
    };
  };

  const prepaymentDialogTotals = prepaymentDialogEvent
    ? getEventPrepaymentTotals(prepaymentDialogEvent, prepaymentTotalsByEventId)
    : { practiceTotal: 0, totalDeposits: 0, totalAdvances: 0, totalPrepayments: 0, amountDue: 0 };
  const savedDetailPrepaymentTotals = editingEvent
    ? getEventPrepaymentTotals(editingEvent, prepaymentTotalsByEventId)
    : { practiceTotal: 0, totalDeposits: 0, totalAdvances: 0, totalPrepayments: 0, amountDue: 0 };
  const detailPrepaymentTotals = editingEvent
    ? {
        ...savedDetailPrepaymentTotals,
        practiceTotal: formServicesTotal,
        amountDue: Math.max(0, formServicesTotal - savedDetailPrepaymentTotals.totalPrepayments),
      }
    : savedDetailPrepaymentTotals;
  const showDetailPrepaymentSummary = Boolean(editingEvent);
  const detailPrepaymentSummaryItems = [
    {
      key: "eventTotal",
      label: t("congressReservations.prepayments.eventTotal"),
      value: formatMoney(detailPrepaymentTotals.practiceTotal),
    },
    {
      key: "totalPrepayments",
      label: t("congressReservations.prepayments.totalPrepayments"),
      value: formatMoney(detailPrepaymentTotals.totalPrepayments),
    },
    {
      key: "deposits",
      label: t("congressReservations.prepayments.totalDeposits"),
      value: formatMoney(detailPrepaymentTotals.totalDeposits),
    },
    {
      key: "advances",
      label: t("congressReservations.prepayments.totalAdvances"),
      value: formatMoney(detailPrepaymentTotals.totalAdvances),
    },
    {
      key: "amountDue",
      label: t("congressReservations.prepayments.amountDue"),
      value: formatMoney(detailPrepaymentTotals.amountDue),
    },
  ];
  const prepaymentDialogMovements = prepaymentDialogEvent
    ? normalizePrepaymentMovements(prepaymentDialogEvent, prepaymentMovementsByEventId)
    : [];
  const refundablePrepaymentSources = prepaymentDialogMovements.filter((movement) => (
    movement.operation === "RECEIPT"
    && prepaymentMovementAvailableAmount(movement, prepaymentDialogMovements) > 0
  ));
  const selectedRefundSource = refundablePrepaymentSources.find((movement) => Number(movement.id) === Number(prepaymentForm.sourceMovementId));
  const selectedRefundAvailableAmount = selectedRefundSource
    ? prepaymentMovementAvailableAmount(selectedRefundSource, prepaymentDialogMovements)
    : 0;
  const selectedRefundIsPlainReceipt = selectedRefundSource?.documentType === "PLAIN_RECEIPT";
  const selectedRefundIsReverse = Boolean(selectedRefundSource && !selectedRefundIsPlainReceipt);
  const prepaymentRefundDocumentOptions = refundDocumentTypesForMovement(selectedRefundSource);
  const requiredPrepaymentDocumentFields = prepaymentOperation === "REFUND"
    ? ["COMMERCIAL_CANCELLATION", "COMMERCIAL_RETURN"].includes(prepaymentForm.documentType)
    : prepaymentForm.type === "ADVANCE" && prepaymentForm.documentType === "COMMERCIAL_DOCUMENT";
  const requiresPrepaymentVatNature = prepaymentOperation !== "REFUND"
    && prepaymentForm.type === "ADVANCE"
    && prepaymentForm.documentType === "INVOICE_TD02"
    && Number(prepaymentForm.vatRate) === 0;

  const renderPracticeActions = (event) => {
    const hasPrepaymentHistory = normalizePrepaymentMovements(event, prepaymentMovementsByEventId).length > 0;
    const prepaymentStateKnown = hasEventPrepaymentTotals(event, prepaymentTotalsByEventId);
    const deleteDisabled = Boolean(event?.hasIssuedAccount || hasPrepaymentHistory || !prepaymentStateKnown);
    const deleteDisabledReason = event?.hasIssuedAccount
      ? t("congressReservations.practiceDeleteBlockedByAccount", { defaultValue: "La pratica non può essere eliminata perché è già stato emesso un conto." })
      : hasPrepaymentHistory
        ? t("congressReservations.practiceDeleteBlockedByPrepayments")
        : !prepaymentStateKnown || prepaymentSummariesLoading
          ? t("congressReservations.prepayments.loading", { defaultValue: "Verifica acconti in corso..." })
          : "";

    return (
    <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }} flexWrap="wrap" useFlexGap>
      {event.status === "PREVENTIVO" && <Button size="small" onClick={() => handleConvertToReservation(event)}>{t("converti")}</Button>}
      {event.status === "TO_CONFIRM" && <Button size="small" variant="contained" onClick={() => handleConfirm(event)}>{t("conferma")}</Button>}
      {event.status === "CONFIRMED" && (
        <>
          <Tooltip title={!isEventArrivalToday(event?.days) ? t("congressReservations.checkInArrivalDateOnly", { defaultValue: "Il check-in è consentito solo nella data di arrivo del congresso." }) : ""}>
            <span>
              <Button size="small" variant="contained" color="warning" disabled={!isEventArrivalToday(event?.days)} onClick={() => handleCheckIn(event)}>
                {t("checkIn", { defaultValue: "Check-in" })}
              </Button>
            </span>
          </Tooltip>
        </>
      )}
      {event.status === "CHECKED_IN" && (
        <>
          <Button size="small" variant="outlined" color="warning" onClick={() => handleRestoreCheckIn(event)}>
            {t("congressReservations.restoreCheckIn")}
          </Button>
          <Tooltip title={t("congressReservations.goToCheckout")}>
            <IconButton size="small" onClick={() => handleCheckout(event)}><PointOfSaleIcon /></IconButton>
          </Tooltip>
        </>
      )}
      <Button size="small" onClick={() => openPrepaymentDialog(event)}>
        {t("congressReservations.prepayments.action")}
      </Button>
      <Tooltip title="Conto proforma">
        <IconButton size="small" onClick={() => openProformaDialog(event)}>
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Button size="small" onClick={() => openEditEvent(event)}>{t("modifica")}</Button>
      <Tooltip title={deleteDisabledReason}>
        <span>
          <Button size="small" color="error" disabled={deleteDisabled} onClick={() => handleDelete(event)}>{t("elimina")}</Button>
        </span>
      </Tooltip>
    </Stack>
    );
  };

  const handlePrintPlanner = () => {
    document.body.classList.add("printing-congress-planner");
    const cleanup = () => document.body.classList.remove("printing-congress-planner");
    window.addEventListener("afterprint", cleanup, { once: true });
    window.setTimeout(() => {
      window.print();
      window.setTimeout(cleanup, 1000);
    }, 0);
  };

  return (
    <SidebarLayout className="admin-responsive-page">
      <Box className="congress-reservations-page" sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: surfaceBg, minHeight: "100vh", maxWidth: "100vw", overflowX: "hidden" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={800}>{t("planner")}</Typography>
            <Typography color="text.secondary">{t("plannersubtitle")}</Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              type="date"
              label={t("congressReservations.plannerStartDate", { defaultValue: "Data di partenza" })}
              value={toLocalDateInput(plannerDate)}
              onChange={(event) => {
                if (!event.target.value) return;
                const selectedDate = new Date(`${event.target.value}T12:00:00`);
                if (!Number.isNaN(selectedDate.getTime())) setPlannerDate(selectedDate);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: 175,
                bgcolor: infoBg,
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: infoBorder },
                "& .MuiInputLabel-root": { color: "info.main", fontWeight: 800 },
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                p: 0.5,
                borderRadius: 2,
                bgcolor: infoBg,
                border: "1px solid",
                borderColor: infoBorder,
              }}
            >
              <Button size="small" color="info" variant="contained" onClick={() => setPlannerDate(new Date())}>{t("oggi")}</Button>
              <Button size="small" color="info" variant="text" onClick={() => setPlannerDate((prev) => addDays(prev, -plannerStepDays))}>{t("indietro")}</Button>
              <Button size="small" color="info" variant="text" onClick={() => setPlannerDate((prev) => addDays(prev, plannerStepDays))}>{t("avanti")}</Button>
            </Box>
            {PLANNER_MODES.map((mode) => (
              <Button
                key={mode.value}
                variant={plannerMode === mode.value ? "contained" : "outlined"}
                onClick={() => setPlannerMode(mode.value)}
              >
                {t(mode.labelKey)}
              </Button>
            ))}
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintPlanner}>
              {t("congressReservations.printPlanner", { defaultValue: "Print planner" })}
            </Button>
            <Button variant="contained" onClick={() => { resetForm(); setDialogOpen(true); }}>{t("nuovapratica")}</Button>
            <IconButton onClick={loadData}><RefreshIcon /></IconButton>
          </Stack>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {isDepartmentRestricted && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Vista operativa filtrata sui tuoi reparti: {restrictedDepartmentNames || "-"}.
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={800}>{t("filtri")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("filtriPlannerHelp")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label={t("stato")} value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}>
                <MenuItem value="">{t("tutti")}</MenuItem>
                {SEARCHABLE_STATUS_ENTRIES.map(([value, labelKey]) => (
  <MenuItem key={value} value={value}>
    {t(labelKey)}
  </MenuItem>
))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label={t("sala")} value={filters.tableId} onChange={(e) => setFilters((prev) => ({ ...prev, tableId: e.target.value }))}>
                <MenuItem value="">{t("tutti")}</MenuItem>
                {tables.map((table) => <MenuItem key={table.id} value={table.id}>{table.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label={t("clienteaziendaevento")} value={filters.recipient} onChange={(e) => setFilters((prev) => ({ ...prev, recipient: e.target.value }))} />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField fullWidth type="date" label={t("da")} value={filters.from} onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField fullWidth type="date" label={t("a")} value={filters.to} onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={5}>
              <Autocomplete
                multiple
                options={categoryOptions}
                value={categoryOptions.filter((category) => filters.categoryIds.includes(category.id))}
                onChange={(_, value) => setFilters((prev) => ({ ...prev, categoryIds: value.map((category) => category.id) }))}
                getOptionLabel={(option) => option?.name || ""}
                renderInput={(params) => <TextField {...params} label={t("categorieprodottinelreport")} />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                sx={{ height: "100%", alignItems: "center", ml: 0 }}
                control={
                  <Checkbox
                    checked={Boolean(filters.hidePrices)}
                    onChange={(e) => setFilters((prev) => ({ ...prev, hidePrices: e.target.checked }))}
                  />
                }
                label={t("pdfsenzaprezzietotali")}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button fullWidth variant="outlined" sx={{ height: "100%" }} onClick={() => setFilters({ status: "", tableId: "", recipient: "", from: "", to: "", categoryIds: [], hidePrices: false, showCancelledOnBoard: false })}>
                {t("pulisci")}
              </Button>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button fullWidth variant="contained" sx={{ height: "100%" }} onClick={handleDownloadSummaryPdf}>PDF</Button>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button fullWidth variant="outlined" sx={{ height: "100%" }} onClick={() => setSummaryEmailOpen(true)}>Email</Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper
          className="congress-planner-print-area"
          data-planner-mode={plannerMode}
          sx={{ overflow: "hidden", borderRadius: 3, border: `1px solid ${borderColor}`, position: "relative", maxWidth: "100%" }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${borderColor}`,
              bgcolor: surfacePaper,
              boxShadow: `0 8px 18px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.28 : 0.08)}`,
            }}
          >
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
              <Box>
                <Typography fontWeight={900}>
                  {plannerMode === "DAY" || plannerMode === "AGENDA"
                    ? formatDateLabel(plannerDate, language)
                    : `${formatDayLabel(plannerDays[0], language)} - ${formatDayLabel(plannerDays[plannerDays.length - 1], language)}`}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" color="text.secondary">
                    {t("modalita")}: {t(PLANNER_MODES.find((mode) => mode.value === plannerMode)?.labelKey || "")}
                  </Typography>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={Boolean(filters.showCancelledOnBoard)}
                        onChange={(e) => setFilters((prev) => ({ ...prev, showCancelledOnBoard: e.target.checked }))}
                      />
                    }
                    label={t("mostrannullateneltabellone")}
                  />
                </Stack>
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              maxWidth: "100%",
              overflowX: plannerMode === "AGENDA" ? "visible" : "auto",
              overflowY: plannerMode === "AGENDA" ? "visible" : { xs: "visible", md: "auto" },
              maxHeight: plannerMode === "AGENDA" ? "none" : { xs: "none", md: "calc(100vh - 260px)" },
              WebkitOverflowScrolling: "touch",
              position: "relative",
            }}
          >
          {plannerMode === "AGENDA" ? (
            <Stack spacing={1.5} sx={{ p: { xs: 1.5, md: 2 } }}>
              {agendaRows.map(({ event, day }) => (
                <Paper
                  key={`${event.id}-${day.id}`}
                  onClick={() => openEditEvent(event)}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    cursor: "pointer",
                    border: `1px solid ${borderColor}`,
                    bgcolor: getEventCardStyles(event).bgcolor,
                  }}
                >
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between">
                    <Box>
                      <Typography fontWeight={900}>{formatDateTime(day.startDate)} - {formatDateTime(day.endDate)}</Typography>
                      <Typography fontWeight={800}>{event.eventName}</Typography>
                      <Typography variant="body2" color="text.secondary">{eventRecipient(event)}</Typography>
                    </Box>
                    <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                      {renderStatusChip(event)}
                      {departmentScopeSummary(event) && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {departmentScopeSummary(event)}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ mt: 0.5 }}>{collectTableNames(event).join(", ") || "-"}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              ))}
              {agendaRows.length === 0 && <Typography color="text.secondary">{t("nessunapractica")}</Typography>}
            </Stack>
          ) : (
            <>
            <Stack className="congress-planner-mobile-view" spacing={1.5} sx={{ display: { xs: "flex", md: "none" }, p: 1.5 }}>
              {boardTables.map((table) => (
                <Paper key={table.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, minWidth: 0 }}>
                  <Stack spacing={1.25}>
                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={900} sx={{ overflowWrap: "anywhere" }}>{table.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{t("capienza")} {table.maxSeats || 0}</Typography>
                        {table.room?.name && (
                          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                            {table.room.name}
                          </Typography>
                        )}
                      </Box>
                      <Button size="small" onClick={(e) => { e.stopPropagation(); openSetupManager(table); }}>
                        {t("setups.shortLabel", { defaultValue: "Allestimenti" })}
                      </Button>
                    </Stack>

                    {plannerDays.map((day) => {
                      const cellEvents = eventsForCell(table.id, day);
                      return (
                        <Box
                          key={`${table.id}-${day.toISOString()}-mobile`}
                          onClick={() => openCreateFromSlot(table, day)}
                          sx={{
                            p: 1.25,
                            border: `1px solid ${subtleBorder}`,
                            borderRadius: 1.5,
                            background: softGridBg,
                            cursor: "pointer",
                          }}
                        >
                          <Typography fontWeight={800} sx={{ mb: 1 }}>
                            {formatDayLabel(day, language)}
                          </Typography>
                          <Stack spacing={1}>
                            {cellEvents.map(({ event, display }) => {
                              const dayInfo = display?.day;
                              const isRelatedBlock = Boolean(display?.isRelatedBlock);
                              const cardStyles = getEventCardStyles(event, isRelatedBlock);
                              return (
                                <Paper
                                  key={`${event.id}-${dayInfo?.id}-${table.id}-mobile-${isRelatedBlock ? "related" : "direct"}`}
                                  onClick={(e) => { e.stopPropagation(); openEditEvent(event); }}
                                  sx={{
                                    p: 1,
                                    borderRadius: 1.5,
                                    minWidth: 0,
                                    ...cardStyles,
                                  }}
                                >
                                  <Typography variant="caption" fontWeight={800} display="block">
                                    {formatDateTime(dayInfo?.startDate)} - {formatDateTime(dayInfo?.endDate)}
                                  </Typography>
                                  <Typography fontWeight={800} sx={{ overflowWrap: "anywhere" }}>{event.eventName}</Typography>
                                  {isRelatedBlock && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {t("bloccatada")} {display?.sourceTable?.name || "sala collegata"}
                                    </Typography>
                                  )}
                                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                    {renderStatusChip(event)}
                                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                                      {eventRecipient(event)}
                                    </Typography>
                                  </Stack>
                                </Paper>
                              );
                            })}
                            {cellEvents.length === 0 && (
                              <Typography variant="body2" color="text.secondary">
                                {t("nessunapractica")}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              ))}
            </Stack>

            <Box className="congress-planner-desktop-view" sx={{ display: { xs: "none", md: "block" } }}>
            {plannerMode === "DAY" ? (
            <Box sx={{ minWidth: 1450 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `210px repeat(${dayTimeSlots.length}, minmax(92px, 1fr))`,
                  bgcolor: softHeaderBg,
                  borderBottom: `1px solid ${borderColor}`,
                  position: "sticky",
                  top: 0,
                  zIndex: 35,
                  boxShadow: `0 6px 14px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.24 : 0.07)}`,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    fontWeight: 800,
                    bgcolor: softHeaderBg,
                    borderRight: `1px solid ${borderColor}`,
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                  }}
                >
                  {t("sala")}
                </Box>
                {dayTimeSlots.map((slot) => (
                  <Box key={slot.toISOString()} sx={{ p: 1.25, textAlign: "center", fontWeight: 800, borderLeft: `1px solid ${borderColor}`, bgcolor: softHeaderBg }}>
                    {formatTimeLabel(slot)}
                  </Box>
                ))}
              </Box>
              {boardTables.map((table) => {
                const tableDayEvents = eventsForTimeSlot(table.id, plannerDate);
                const laidOutEvents = layoutDayEvents(tableDayEvents);
                const laneCount = Math.max(1, ...laidOutEvents.map((item) => item.laneCount || 1));
                const laneHeight = 112;
                const rowHeight = Math.max(136, laneCount * laneHeight + 20);
                return (
                  <Box
                    key={table.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: `210px repeat(${dayTimeSlots.length}, minmax(92px, 1fr))`,
                      gridTemplateRows: `${rowHeight}px`,
                      borderBottom: `1px solid ${subtleBorder}`,
                    }}
                  >
                    <Box sx={{ p: 2, bgcolor: softCellBg, borderRight: `1px solid ${borderColor}`, gridColumn: "1 / 2", gridRow: "1", position: "sticky", left: 0, zIndex: 5 }}>
                      <Typography fontWeight={800}>{table.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{t("capienza")} {table.maxSeats || 0}</Typography>
                      <Typography variant="caption" color="text.secondary">{table.room?.name || ""}</Typography>
                    </Box>
                    {dayTimeSlots.map((slot, index) => (
                      <Box
                        key={`${table.id}-${slot.toISOString()}`}
                        onClick={() => openCreateFromSlot(table, slot, timeInputFromDate(slot), timeInputFromDate(addHours(slot, 1)))}
                        sx={{
                          gridColumn: `${index + 2} / ${index + 3}`,
                          gridRow: "1",
                          minHeight: rowHeight,
                          cursor: "pointer",
                          borderLeft: `1px solid ${subtleBorder}`,
                          background: softGridBg,
                          "&:hover": { bgcolor: softGridHover },
                        }}
                      />
                    ))}
                    {laidOutEvents.map(({ event, day, position, laneIndex, isRelatedBlock, sourceTable }) => {
                      const cardStyles = getEventCardStyles(event, isRelatedBlock);
                      return (
                        <Paper
                          key={`${event.id}-${day.id}-${table.id}-${isRelatedBlock ? "related" : "direct"}`}
                          onClick={(e) => { e.stopPropagation(); openEditEvent(event); }}
                          sx={{
                            gridColumn: position.gridColumn,
                            gridRow: "1",
                            zIndex: 2,
                            alignSelf: "start",
                            m: 0.75,
                            mt: `${10 + laneIndex * laneHeight}px`,
                            minHeight: 88,
                            p: 1,
                            borderRadius: 1.5,
                            cursor: "pointer",
                            overflow: "hidden",
                            ...cardStyles,
                          }}
                        >
                          <Typography variant="caption" fontWeight={900} display="block">
                            {formatTimeLabel(new Date(day.startDate))} - {formatTimeLabel(new Date(day.endDate))}
                          </Typography>
                          <Typography variant="body2" fontWeight={900} noWrap>{event.eventName}</Typography>
                          {isRelatedBlock && (
                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                              {t("bloccatada")} {sourceTable?.name || "sala collegata"}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                            {renderStatusChip(event)}
                            <Typography variant="caption" color="text.secondary" noWrap>{eventRecipient(event)}</Typography>
                          </Stack>
                          {departmentScopeSummary(event) && (
                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                              {departmentScopeSummary(event)}
                            </Typography>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box
              className="congress-planner-multiday-grid"
              sx={{
                minWidth: Math.max(1200, 210 + plannerDays.length * 160),
                "--planner-day-count": plannerDays.length,
              }}
            >
              <Box
                className="congress-planner-grid-row congress-planner-grid-header"
                sx={{
                  display: "grid",
                  gridTemplateColumns: `210px repeat(${plannerDays.length}, minmax(150px, 1fr))`,
                  bgcolor: softHeaderBg,
                  borderBottom: `1px solid ${borderColor}`,
                  position: "sticky",
                  top: 0,
                  zIndex: 35,
                  boxShadow: `0 6px 14px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.24 : 0.07)}`,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    fontWeight: 800,
                    bgcolor: softHeaderBg,
                    borderRight: `1px solid ${borderColor}`,
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                  }}
                >
                  {t("sala")}
                </Box>
                {plannerDays.map((day) => (
  <Box key={day.toISOString()} sx={{ p: 2, textAlign: "center", fontWeight: 800, borderLeft: `1px solid ${borderColor}`, bgcolor: softHeaderBg }}>
    {formatDayLabel(day, language)}
  </Box>
                ))}
              </Box>
              {boardTables.map((table) => (
                <Box
                  key={table.id}
                  className="congress-planner-grid-row congress-planner-room-row"
                  sx={{ display: "grid", gridTemplateColumns: `210px repeat(${plannerDays.length}, minmax(150px, 1fr))`, minHeight: 170, borderBottom: `1px solid ${subtleBorder}` }}
                >
                  <Box sx={{ p: 2, bgcolor: softCellBg, borderRight: `1px solid ${borderColor}`, position: "sticky", left: 0, zIndex: 5 }}>
                    <Typography fontWeight={800}>{table.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{t("capienza")} {table.maxSeats || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">{table.room?.name || ""}</Typography>
                    <Button size="small" sx={{ mt: 1 }} onClick={(e) => { e.stopPropagation(); openSetupManager(table); }}>
                      {t("setups.shortLabel", { defaultValue: "Allestimenti" })}
                    </Button>
                  </Box>
                  {plannerDays.map((day) => {
                    const cellEvents = eventsForCell(table.id, day);
                    return (
                      <Box
                        key={`${table.id}-${day.toISOString()}`}
                        onClick={() => openCreateFromSlot(table, day)}
                        sx={{
                          p: 1,
                          minWidth: 0,
                          cursor: "pointer",
                          borderLeft: `1px solid ${subtleBorder}`,
                          background: softGridBg,
                          "&:hover": { bgcolor: softGridHover },
                        }}
                      >
                        <Stack spacing={1}>
                          {cellEvents.map(({ event, display }) => {
                            const dayInfo = display?.day;
                            const isRelatedBlock = Boolean(display?.isRelatedBlock);
                            const cardStyles = getEventCardStyles(event, isRelatedBlock);
                            return (
                              <Paper
                                key={`${event.id}-${dayInfo?.id}-${table.id}-${isRelatedBlock ? "related" : "direct"}`}
                                onClick={(e) => { e.stopPropagation(); openEditEvent(event); }}
                                sx={{
                                  p: 1,
                                  minWidth: 0,
                                  borderRadius: 2,
                                  ...cardStyles,
                                }}
                              >
                                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between">
                                  <Typography variant="caption" fontWeight={800}>{formatDateTime(dayInfo?.startDate)} - {formatDateTime(dayInfo?.endDate)}</Typography>
                                  {renderStatusChip(event)}
                                </Stack>
                                <Typography fontWeight={800} noWrap>{event.eventName}</Typography>
                                {isRelatedBlock && (
                                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                    {t("bloccatada")} {display?.sourceTable?.name || "sala collegata"}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" noWrap>{eventRecipient(event)}</Typography>
                                {departmentScopeSummary(event) && (
                                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                    {departmentScopeSummary(event)}
                                  </Typography>
                                )}
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          )}
            </Box>
            </>
          )}
          </Box>
        </Paper>

        <Paper sx={{ p: 2, mt: 2, borderRadius: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={800}>{t("riepilogoPratiche")}</Typography>
              <Typography variant="body2" color="text.secondary">
                {practiceSummaryEvents.length} {t("pratichetrovatesu")} {events.length}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => setPracticeSummaryFilters({ q: "", status: "", practiceType: "", tableId: "" })}
            >
              {t("Pulisicifiltrilista")}
            </Button>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("cerca")}
                placeholder={t("placeholdercerca")}
                value={practiceSummaryFilters.q}
                onChange={(e) => setPracticeSummaryFilters((prev) => ({ ...prev, q: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <TextField
                select
                fullWidth
                label={t("stato")}
                value={practiceSummaryFilters.status}
                onChange={(e) => setPracticeSummaryFilters((prev) => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="">{t("tutti")}</MenuItem>
                {SEARCHABLE_STATUS_ENTRIES.map(([value, labelKey]) => (
  <MenuItem key={value} value={value}>
    {t(labelKey)}
  </MenuItem>
))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2.5}>
              <TextField
                select
                fullWidth
                label={t("tipoPratica")}
                value={practiceSummaryFilters.practiceType}
                onChange={(e) => setPracticeSummaryFilters((prev) => ({ ...prev, practiceType: e.target.value }))}
              >
                <MenuItem value="">{t("tutti")}</MenuItem>
                {PRACTICE_TYPES.map((item) => <MenuItem key={item.value} value={item.value}>{t(item.labelKey)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label={t("sala")}
                value={practiceSummaryFilters.tableId}
                onChange={(e) => setPracticeSummaryFilters((prev) => ({ ...prev, tableId: e.target.value }))}
              >
                <MenuItem value="">{t("tutti")}</MenuItem>
                {tables.map((table) => <MenuItem key={table.id} value={table.id}>{table.name}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>

          <Stack spacing={1.25} sx={{ display: { xs: "flex", md: "none" } }}>
            {pagedPracticeSummaryEvents.map((event) => (
              <Paper key={event.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, minWidth: 0 }}>
                <Stack spacing={1}>
                  <Box>
                    <Typography fontWeight={800} sx={{ overflowWrap: "anywhere" }}>{event.eventName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {capitalizeFirst(t(PRACTICE_TYPES.find((item) => item.value === event.practiceType)?.labelKey || event.practiceType))}
                    </Typography>
                    {collectUsageTypeNames(event).length > 0 && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {t("uso")} {collectUsageTypeNames(event).join(", ")}
                      </Typography>
                    )}
                  </Box>
                  <Box>{renderStatusChip(event)}</Box>
                  {event.status === "CANCELLED" && event.cancellationReason && (
                    <Typography variant="caption" color="text.secondary">
                      {t("motivo")} {event.cancellationReason}
                    </Typography>
                  )}
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">{t("destinatario")}</Typography>
                      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{eventRecipient(event)}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">{t("sala")}</Typography>
                      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{collectTableNames(event).join(", ") || "-"}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">{t("periodo")}</Typography>
                      <Typography variant="body2">{formatDateTime(event.days?.[0]?.startDate)} - {formatDateTime(event.days?.[event.days.length - 1]?.endDate)}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">{t("invii")}</Typography>
                      <Typography variant="body2">
                        {event.quoteSendCount || 0}
                        {event.quoteSendCount > 1
                          ? ` ${t("congressReservations.resends")}`
                          : event.quoteSendCount === 1
                            ? ` ${t("congressReservations.send")}`
                            : ""}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("congressReservations.prepayments.column")}
                      </Typography>
                      {!hasEventPrepaymentTotals(event, prepaymentTotalsByEventId) && prepaymentSummariesLoading ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Stack spacing={0.25}>
                          <Typography variant="body2">
                            {t("congressReservations.prepayments.eventTotal", { defaultValue: "Total account" })}: {formatMoney(getEventPrepaymentTotals(event, prepaymentTotalsByEventId).practiceTotal)}
                          </Typography>
                          <Typography variant="body2">
                            {t("congressReservations.prepayments.totalPrepayments", { defaultValue: "Total advances/deposits" })}: {formatMoney(getEventPrepaymentTotals(event, prepaymentTotalsByEventId).totalPrepayments)}
                          </Typography>
                          <Typography variant="body2">
                            {t("congressReservations.prepayments.amountDue", { defaultValue: "Residual balance" })}: {formatMoney(getEventPrepaymentTotals(event, prepaymentTotalsByEventId).amountDue)}
                          </Typography>
                        </Stack>
                      )}
                    </Grid>
                  </Grid>
                  {renderPracticeActions(event)}
                </Stack>
              </Paper>
            ))}
            {practiceSummaryEvents.length === 0 && (
              <Typography color="text.secondary">{t("nessunapraticatrovata")}</Typography>
            )}
          </Stack>

          <TableContainer sx={{ display: { xs: "none", md: "block" }, maxWidth: "100%" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    ["practice", t("pratica")],
                    ["status", t("stato")],
                    ["recipient", t("destinatario")],
                    ["table", t("sala")],
                    ["period", t("periodo")],
                    ["sendCount", t("invii")],
                    ["prepayments", t("congressReservations.prepayments.column")],
                  ].map(([key, label]) => (
                    <TableCell key={key} sortDirection={practiceSummarySort.key === key ? practiceSummarySort.direction : false}>
                      <TableSortLabel
                        active={practiceSummarySort.key === key}
                        direction={practiceSummarySort.key === key ? practiceSummarySort.direction : "asc"}
                        onClick={() => handlePracticeSummarySort(key)}
                        sx={{
                          fontWeight: 800,
                          "& .MuiTableSortLabel-icon": {
                            opacity: practiceSummarySort.key === key ? 1 : 0.4,
                          },
                        }}
                      >
                        {label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{t("azioni")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedPracticeSummaryEvents.map((event) => (
                  <TableRow key={event.id} hover>
                    <TableCell>
                      <Typography fontWeight={800}>{event.eventName}</Typography>
                      <Typography variant="caption">{capitalizeFirst(t(PRACTICE_TYPES.find((item) => item.value === event.practiceType)?.labelKey || event.practiceType))}</Typography>
                      {collectUsageTypeNames(event).length > 0 && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {t("uso")} {collectUsageTypeNames(event).join(", ")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {renderStatusChip(event)}
                      {event.status === "CANCELLED" && event.cancellationReason && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {t("motivo")} {event.cancellationReason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{eventRecipient(event)}</TableCell>
                    <TableCell>{collectTableNames(event).join(", ") || "-"}</TableCell>
                    <TableCell>{formatDateTime(event.days?.[0]?.startDate)} - {formatDateTime(event.days?.[event.days.length - 1]?.endDate)}</TableCell>
                    <TableCell>
                      {event.quoteSendCount || 0}
                      {event.quoteSendCount > 1
                        ? ` ${t("congressReservations.resends")}`
                        : event.quoteSendCount === 1
                          ? ` ${t("congressReservations.send")}`
                          : ""}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        if (!hasEventPrepaymentTotals(event, prepaymentTotalsByEventId) && prepaymentSummariesLoading) {
                          return <CircularProgress size={16} />;
                        }
                        const totals = getEventPrepaymentTotals(event, prepaymentTotalsByEventId);
                        return (
                          <Stack spacing={0.25}>
                            <Typography variant="body2">
                              {t("congressReservations.prepayments.eventTotal", { defaultValue: "Total account" })}: <strong>{formatMoney(totals.practiceTotal)}</strong>
                            </Typography>
                            <Typography variant="body2">
                              {t("congressReservations.prepayments.totalPrepayments", { defaultValue: "Total advances/deposits" })}: <strong>{formatMoney(totals.totalPrepayments)}</strong>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t("congressReservations.prepayments.remainingShort", { amount: formatMoney(totals.amountDue) })}
                            </Typography>
                          </Stack>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      {renderPracticeActions(event)}
                    </TableCell>
                  </TableRow>
                ))}
                {practiceSummaryEvents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>{t("nessunapraticatrovata")}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={practiceSummaryEvents.length}
            page={practiceSummaryPage}
            onPageChange={(_, page) => setPracticeSummaryPage(page)}
            rowsPerPage={practiceSummaryRowsPerPage}
            onRowsPerPageChange={(e) => {
              setPracticeSummaryRowsPerPage(parseInt(e.target.value, 10));
              setPracticeSummaryPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage= {t("righeperpagina")}
            labelDisplayedRows={({ from, to, count }) =>
              t("common.pagination.displayedRows", { from, to, count: count !== -1 ? count : `${t("congressReservations.moreThan")} ${to}` })
            }
          />
        </Paper>

        <Dialog open={Boolean(proformaDialogEvent)} onClose={closeProformaDialog} fullWidth maxWidth="md">
          <DialogTitle>Conto proforma</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Box>
                <Typography fontWeight={800}>{proformaDialogEvent?.eventName || "-"}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {eventRecipient(proformaDialogEvent || {}) || "-"}
                </Typography>
              </Box>
              {(proformaDialogEvent?.days || []).map((day, dayIndex) => {
                const dayId = Number(day.id);
                const daySelected = proformaSelectedDayIds.includes(dayId);
                const rows = proformaRowsForDay(day);
                return (
                  <Paper key={day.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={daySelected}
                            onChange={() => toggleProformaDay(dayId)}
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight={800}>
                              Giornata {dayIndex + 1}: {formatDateTime(day.startDate)} - {formatDateTime(day.endDate)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {collectTableNames({ days: [day] }).join(", ") || "-"}
                            </Typography>
                          </Box>
                        }
                      />
                      <Stack spacing={0.5} sx={{ pl: { xs: 0, sm: 4 } }}>
                        {rows.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Nessun servizio o pacchetto associato.
                          </Typography>
                        ) : rows.map((row) => (
                          <FormControlLabel
                            key={row.key}
                            control={
                              <Checkbox
                                checked={daySelected || proformaSelectedRowKeys.includes(row.key)}
                                disabled={daySelected}
                                onChange={() => toggleProformaRow(row.key)}
                              />
                            }
                            label={
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" sx={{ width: "100%" }}>
                                <Typography variant="body2">{row.label}</Typography>
                                <Typography variant="body2" color="text.secondary">{formatMoney(row.amount)}</Typography>
                              </Stack>
                            }
                            sx={{ alignItems: "flex-start", mr: 0 }}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeProformaDialog} disabled={proformaDownloading}>
              {t("annulla", { defaultValue: "Annulla" })}
            </Button>
            <Button
              variant="contained"
              onClick={handleDownloadProformaPdf}
              disabled={proformaDownloading}
            >
              {proformaDownloading ? "Creazione..." : "Scarica PDF"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={dialogOpen}
          onClose={() => {
            if (!savingPractice) setDialogOpen(false);
          }}
          disableEscapeKeyDown={savingPractice}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>{editingEvent ? t("congressReservations.editPractice") : t("congressReservations.newPractice")}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField select fullWidth label={t("tipoPractica")} value={form.practiceType} onChange={(e) => updateFormField("practiceType", e.target.value)}>
                    {PRACTICE_TYPES.map((item) => <MenuItem key={item.value} value={item.value}>{capitalizeFirst(t(item.labelKey))}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label={t("nomeEvento")} value={form.eventName} onChange={(e) => updateFormField("eventName", e.target.value)} />
                </Grid>
                {form.practiceType !== "PREVENTIVO" && <Grid item xs={12} md={2.5}>
                  <TextField
                    select
                    fullWidth
                    label={t("stato")}
                    value={form.status}
                    disabled={["CHECKED_IN", "COMPLETED"].includes(form.status)}
                    helperText={["CHECKED_IN", "COMPLETED"].includes(form.status) ? t("reservations_details.completed_locked", { defaultValue: "Lo stato viene gestito automaticamente." }) : ""}
                    onChange={(e) => updateFormField("status", e.target.value)}
                  >
                    <MenuItem value="TO_CONFIRM">{t("daconfermare")}</MenuItem>
                    <MenuItem value="CONFIRMED">{t("confermata")}</MenuItem>
                    {["CHECKED_IN", "COMPLETED"].includes(form.status) && (
                      <MenuItem value={form.status} sx={{ display: "none" }}>
                        {t(STATUS_LABEL_KEYS[form.status] || form.status)}
                      </MenuItem>
                    )}
                    <MenuItem value="CANCELLED">{t("annullata")}</MenuItem>
                    <MenuItem value="NO_SHOW">{t("noshow")}</MenuItem>
                  </TextField>
                </Grid>}
                <Grid item xs={12} md={2.5}>
                  <TextField
                    fullWidth
                    type="date"
                    label={t("datascadenzapreventivo")}
                    value={form.quoteValidUntil}
                    onChange={(e) => updateFormField("quoteValidUntil", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={form.practiceType !== "PREVENTIVO"}
                  />
                </Grid>
              </Grid>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={t("destinatarioLibero", { defaultValue: "Free recipient" })}
                        value={form.genericRecipientName}
                        onChange={(e) => updateFormField("genericRecipientName", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                      <TextField
                        fullWidth
                        label={t("congressReservations.freeRecipientVat", { defaultValue: "Free recipient VAT" })}
                        value={form.genericRecipientVatNumber}
                        onChange={(e) => updateFormField("genericRecipientVatNumber", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={t("congressReservations.freeRecipientEmail")}
                        value={form.quoteEmail}
                        onChange={(e) => updateFormField("quoteEmail", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} md={1.75}>
                      <Button variant="outlined" fullWidth onClick={handleCreateGenericClient} disabled={loading || !form.genericRecipientName.trim()}>
                        {t("creaCliente", { defaultValue: "Create client" })}
                      </Button>
                    </Grid>
                    <Grid item xs={6} md={1.75}>
                      <Button variant="outlined" fullWidth onClick={handleCreateGenericCompany} disabled={loading || !form.genericRecipientName.trim()}>
                        {t("creaAzienda", { defaultValue: "Create company" })}
                      </Button>
                    </Grid>
                  </Grid>

                  <Divider />

                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        options={clients || []}
                        value={form.client || null}
                        onChange={(_, value) => selectPracticeClient(value)}
                        getOptionLabel={(option) => `${option?.name || ""} ${option?.surname || ""}`.trim()}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        renderInput={(params) => <TextField {...params} label={t("congressReservations.optionalClient", { defaultValue: "Optional client" })} />}
                      />
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                      <TextField
                        fullWidth
                        label={t("congressReservations.clientVat", { defaultValue: "Client VAT" })}
                        value={form.client?.vatNumber || ""}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3.5}>
                      <TextField
                        fullWidth
                        label={t("congressReservations.clientEmail", { defaultValue: "Client email" })}
                        value={form.client?.email || ""}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={(
                          <Switch
                            checked={Boolean(form.clientEmailActive)}
                            disabled={!isEmail(form.client?.email)}
                            onChange={(e) => updateFormField("clientEmailActive", e.target.checked)}
                          />
                        )}
                        label={t("congressReservations.activeFlag", { defaultValue: "Active" })}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        options={companies || []}
                        value={form.company || null}
                        onChange={(_, value) => selectPracticeCompany(value)}
                        getOptionLabel={(option) => option?.companyName || ""}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        renderInput={(params) => <TextField {...params} label={t("congressReservations.optionalCompany", { defaultValue: "Optional company" })} />}
                      />
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                      <TextField
                        fullWidth
                        label={t("congressReservations.companyVat", { defaultValue: "Company VAT" })}
                        value={form.company?.vatNumber || ""}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3.5}>
                      <TextField
                        fullWidth
                        label={t("congressReservations.companyEmail", { defaultValue: "Company email" })}
                        value={form.company?.email || ""}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={(
                          <Switch
                            checked={Boolean(form.companyEmailActive)}
                            disabled={!isEmail(form.company?.email)}
                            onChange={(e) => updateFormField("companyEmailActive", e.target.checked)}
                          />
                        )}
                        label={t("congressReservations.activeFlag", { defaultValue: "Active" })}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>

              <Grid container spacing={2}>
                {form.status === "CANCELLED" && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      required
                      label={t("congressReservations.cancellationReason")}
                      value={form.cancellationReason}
                      onChange={(e) => {
                        updateFormField("cancellationReason", e.target.value);
                        if (cancelReasonError) setCancelReasonError("");
                      }}
                      error={Boolean(cancelReasonError)}
                      helperText={cancelReasonError || t("congressReservations.cancellationReasonHelp")}
                    />
                  </Grid>
                )}
              </Grid>

              <Divider />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight={800}>{t("giornateesala")}</Typography>
                <Button startIcon={<AddIcon />} onClick={addDay}>{t("aggiungidata")}</Button>
              </Stack>
              {checkedInStartDayIndex >= 0 && (
                <Alert severity="info">
                  Dopo il check-in la data e l'ora di inizio non possono essere modificate. Puoi cambiare la sala o la fine dell'evento.
                </Alert>
              )}
              {form.status !== "PREVENTIVO" && form.days.some((day) => !Array.isArray(day.tableIds) || day.tableIds.length === 0) && (
                <Alert severity="warning">
                  {isRestaurantTenant
                    ? "Per salvare o confermare la prenotazione assegna almeno un tavolo a ogni giornata."
                    : t("congressReservations.validation.roomRequiredForReservation")}
                </Alert>
              )}
              {form.days.map((day, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        type="date"
                        label={t("data")}
                        value={day.date}
                        disabled={index === checkedInStartDayIndex}
                        onChange={(e) => updateDay(index, "date", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: checkedInStartDate || undefined }}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TimePicker24Field
                        label={t("da")}
                        value={parseTimePickerValue(day.startTime)}
                        disabled={index === checkedInStartDayIndex}
                        onChange={(value) => updateDay(index, "startTime", formatTimePickerValue(value))}
                        ampm={false}
                        inputFormat="HH:mm"
                        placeholder="09:00"
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TimePicker24Field
                        label={t("a")}
                        value={parseTimePickerValue(day.endTime)}
                        onChange={(value) => updateDay(index, "endTime", formatTimePickerValue(value))}
                        ampm={false}
                        inputFormat="HH:mm"
                        placeholder="10:00"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField fullWidth type="number" label={t("ospiti")} value={day.guests} onChange={(e) => updateDay(index, "guests", e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Autocomplete
                        multiple
                        options={tables}
                        value={tables.filter((table) => day.tableIds.includes(table.id))}
                        onChange={(_, value) => updateDay(index, "tableIds", value.map((item) => item.id))}
                        getOptionLabel={(option) => `${option.name} (${option.maxSeats || 0})`}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={isRestaurantTenant ? "Tavoli assegnati" : t("saleassegnate")}
                            required={form.status !== "PREVENTIVO"}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <IconButton color="error" disabled={form.days.length === 1 || index === checkedInStartDayIndex} onClick={() => removeDay(index)}><DeleteIcon /></IconButton>
                    </Grid>
                    {(day.tableIds || []).map((tableId) => {
                      const table = tables.find((item) => item.id === tableId);
                      const tableSetup = (day.tableSetups || []).find((item) => item.tableId === tableId) || {};
                      const availableSetups = setups.filter((setup) => setup.active !== false || setup.id === tableSetup.setupId);
                      return (
                        <Grid item xs={12} key={`${index}-${tableId}-setup`}>
                          <Box
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1.5,
                              p: 1.25,
                              bgcolor: "background.paper",
                            }}
                          >
                            <Stack
                              direction={{ xs: "column", md: "row" }}
                              spacing={1}
                              alignItems={{ xs: "stretch", md: "center" }}
                            >
                              <Box sx={{ minWidth: { md: 150 }, maxWidth: { md: 190 } }}>
                                <Typography fontWeight={800} noWrap>{table?.name || `Sala ${tableId}`}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {t("capienza")} {table?.maxSeats || 0}
                                </Typography>
                              </Box>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label={t("allestimento", { defaultValue: "Allestimento" })}
                                value={tableSetup.setupId || ""}
                                onChange={(e) => updateDayTableSetup(index, tableId, e.target.value)}
                                sx={{ minWidth: { md: 220 } }}
                              >
                                <MenuItem value="">{t("congressReservations.none")}</MenuItem>
                                {availableSetups.map((setup) => (
                                  <MenuItem key={setup.id} value={setup.id}>{setup.name || `Allestimento ${setup.id}`}</MenuItem>
                                ))}
                              </TextField>
                              <Autocomplete
                                options={usageTypes.filter((item) => item.active !== false || item.id === tableSetup.usageTypeId)}
                                value={usageTypes.find((item) => item.id === tableSetup.usageTypeId) || null}
                                onChange={(_, value) => updateDayTableUsageType(index, tableId, value?.id || "")}
                                getOptionLabel={(option) => option?.name || ""}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                sx={{ minWidth: { md: 240 }, flex: 1 }}
                                renderInput={(params) => <TextField {...params} size="small" label={t("tipiutilizzo")} />}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                label={t("note")}
                                value={tableSetup.note || ""}
                                onChange={(e) => updateDayTableSetupNote(index, tableId, e.target.value)}
                                placeholder={t("congressReservations.setupNotePlaceholder")}
                                sx={{ minWidth: { md: 240 }, flex: 1 }}
                              />
                            </Stack>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              ))}

              <Divider />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight={800}>{t("righepreventivoservizi")}</Typography>
                <Button startIcon={<AddIcon />} onClick={addRow}>{t("aggiungiriga")}</Button>
              </Stack>
              {isDepartmentRestricted && (
                <Alert severity="info">
                  Stai modificando solo le righe dei tuoi reparti: {restrictedDepartmentNames || "-"}.
                  {hiddenFormRowsCount > 0 ? ` ${hiddenFormRowsCount} righe di altri reparti restano salvate nella pratica.` : ""}
                </Alert>
              )}
              {serviceAvailabilityError && (
                <Alert severity="warning">{serviceAvailabilityError}</Alert>
              )}
              {visibleFormRows.map(({ row, index }) => {
                const serviceAvailabilitySummary = getServiceAvailabilitySummary(row);
                return (
                <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <Autocomplete
                        options={catalogRows}
                        value={catalogRows.find((item) => item.type === row.type && item.refId === row.refId) || null}
                        onChange={(_, value) => {
                          if (!value) {
                            updateRow(index, { type: "custom", refId: "", description: "", orario: "", unitPrice: 0, departmentId: "", note: "" });
                            return;
                          }
                          updateRow(index, {
                            type: value.type,
                            refId: value.refId,
                            description: value.description,
                            orario: value.type !== "custom" ? row.orario || "" : "",
                            unitPrice: value.unitPrice,
                          });
                        }}
                        getOptionLabel={(option) => option.label || ""}
                        renderInput={(params) => <TextField {...params} label={t("dacatalogo")} />}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth label={t("descrizionemodificabile")} value={row.description} onChange={(e) => updateRow(index, { description: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        select
                        fullWidth
                        required={row.type === "service" && Number(row.refId) > 0}
                        label={t("reparto", { defaultValue: "Reparto" })}
                        value={row.departmentId || ""}
                        onChange={(e) => updateRow(index, { departmentId: e.target.value })}
                      >
                        <MenuItem value="">{t("congressReservations.none")}</MenuItem>
                        {departments
                          .filter((item) => (!isDepartmentRestricted || restrictedDepartmentIds.includes(Number(item.id)) || Number(item.id) === Number(row.departmentId)))
                          .filter((item) => item.active !== false || Number(item.id) === Number(row.departmentId))
                          .map((item) => (
                            <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                          ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField select fullWidth label={t("giornata")} value={row.dayIndex} onChange={(e) => updateRow(index, { dayIndex: e.target.value })}>
                        <MenuItem value="all">{t("tutte")}</MenuItem>
                        {form.days.map((day, dayIndex) => (
                          <MenuItem key={dayIndex} value={dayIndex}>
                            {day.date ? `${t("giornata")} ${dayIndex + 1} - ${formatFormDayLabel(day.date, language)}` : `${t("giornata")} ${dayIndex + 1}`}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {row.type !== "custom" && Number(row.refId) > 0 && (
                      <Grid item xs={12} md={2}>
                        <TimePicker24Field
                          label={t("ora", { defaultValue: "Ora" })}
                          value={parseTimePickerValue(row.orario)}
                          onChange={(value) => updateRow(index, { orario: formatTimePickerValue(value) })}
                          ampm={false}
                          format="HH:mm"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              placeholder="19:00"
                              inputProps={{
                                ...params.inputProps,
                                readOnly: true,
                                autoComplete: "off",
                                "aria-autocomplete": "none",
                              }}
                              InputProps={{
                                ...params.InputProps,
                                autoComplete: "off",
                              }}
                            />
                          )}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={5}>
                      <Grid container spacing={1.5} alignItems="flex-start">
                        <Grid item xs={6} md={3}>
                          {(() => {
                            const maxGuests = maximumGuestsForServiceRow(row);
                            const exceedsGuests = maxGuests !== null && Number(row.quantity || 0) > maxGuests;
                            return (
                              <TextField
                                fullWidth
                                type="number"
                                label={t("qta")}
                                value={row.quantity}
                                inputProps={{ min: 1 }}
                                error={Boolean(serviceAvailabilitySummary?.hasConflict)}
                                helperText={exceedsGuests ? `Quantità superiore agli ospiti (${maxGuests}): verrà richiesta conferma al salvataggio` : ""}
                                FormHelperTextProps={exceedsGuests ? { sx: { color: "warning.main" } } : undefined}
                                onChange={(e) => {
                                  updateRow(index, { quantity: e.target.value });
                                }}
                              />
                            );
                          })()}
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <TextField fullWidth type="number" label={t("prezzo")} value={row.unitPrice} onChange={(e) => updateRow(index, { unitPrice: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Box sx={{ minHeight: 56, display: "flex", alignItems: "center" }}>
                            <Typography fontWeight={800}>{formatMoney(Number(row.quantity || 0) * Number(row.unitPrice || 0))}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }} alignItems="center" sx={{ minHeight: 56 }}>
                            <Button size="small" variant="outlined" onClick={() => duplicateRow(index)}>
                              {t("duplica")}
                            </Button>
                            <IconButton color="error" disabled={form.rows.length === 1} onClick={() => removeRow(index)}><DeleteIcon /></IconButton>
                          </Stack>
                        </Grid>
                        {serviceAvailabilitySummary && (
                          <Grid item xs={12}>
                            <Tooltip
                              title={serviceAvailabilitySummary.tooltip || ""}
                              disableHoverListener={!serviceAvailabilitySummary.tooltip}
                              placement="top"
                            >
                              <Chip
                                size="small"
                                color={serviceAvailabilitySummary.hasConflict ? "error" : "success"}
                              variant={serviceAvailabilitySummary.hasConflict ? "filled" : "outlined"}
                              label={serviceAvailabilitySummary.label}
                              sx={{
                                height: "auto",
                                minHeight: 24,
                                maxWidth: "100%",
                                "& .MuiChip-label": {
                                  display: "block",
                                  whiteSpace: "normal",
                                  py: 0.25,
                                  fontSize: 11,
                                  lineHeight: 1.35,
                                },
                              }}
                            />
                            </Tooltip>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        label={t("noteRepartoServizio", { defaultValue: "Note per il reparto" })}
                        value={row.note || ""}
                        onChange={(e) => updateRow(index, { note: e.target.value })}
                        placeholder={t("congressReservations.rowNotePlaceholder")}
                      />
                    </Grid>
                  </Grid>
                </Paper>
                );
              })}
              {visibleFormRows.length === 0 && (
                <Alert severity="warning">
                  Nessuna riga appartiene ai tuoi reparti. Puoi aggiungere una nuova riga oppure chiedere a un amministratore di assegnarti il reparto corretto.
                </Alert>
              )}

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.12 : 0.06),
                  borderColor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.4 : 0.24),
                }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
                  <Typography fontWeight={900}>
                    {t("allestimenti.productsTotal", { defaultValue: "Totale servizi" })}
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {formatMoney(formServicesTotal)}
                  </Typography>
                </Stack>
              </Paper>

              {showDetailPrepaymentSummary && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.12 : 0.06),
                    borderColor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.4 : 0.24),
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Typography variant="subtitle1" fontWeight={900}>
                        {t("congressReservations.prepayments.column")}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end" useFlexGap>
                        {loadingPrepaymentSummary && !hasEventPrepaymentTotals(editingEvent, prepaymentTotalsByEventId) && (
                          <CircularProgress size={18} />
                        )}
                        <Button size="small" variant="outlined" onClick={() => openPrepaymentDialog(editingEvent)}>
                          {t("congressReservations.prepayments.action")}
                        </Button>
                      </Stack>
                    </Stack>
                    <Grid container spacing={1.5}>
                      {detailPrepaymentSummaryItems.map((item) => (
                        <Grid item xs={12} sm={6} md={3} key={item.key}>
                          <Box
                            sx={{
                              p: 1.25,
                              minHeight: 72,
                              borderRadius: 1,
                              bgcolor: "background.paper",
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" display="block">
                              {item.label}
                            </Typography>
                            <Typography variant="body1" fontWeight={900}>
                              {item.value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                </Paper>
              )}

              {["PREVENTIVO", "PRENOTAZIONE", "EVENTO"].includes(form.practiceType) && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <MailOutlineIcon />
                      <Typography fontWeight={800}>
                        {form.practiceType === "PREVENTIVO"
                          ? t("Inviopreventivo")
                          : form.practiceType === "PRENOTAZIONE"
                            ? t("invioConfermaOpzione", { defaultValue: "Invio conferma/opzione" })
                            : t("invioRiepilogoEvento", { defaultValue: "Invio riepilogo evento" })}
                      </Typography>
                      <Button variant={form.sendEmail ? "contained" : "outlined"} onClick={togglePracticeEmail}>
                        {form.sendEmail ? t("emailattiva") : t("inviaemaildoposalvataggio")}
                      </Button>
                    </Stack>
                    {form.sendEmail && (
                      <>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(form.includeLogo)}
                              disabled={!tenantLogoBase64}
                              onChange={(e) => updateFormField("includeLogo", e.target.checked)}
                            />
                          }
                          label={t("preventivi.include_logo", { defaultValue: "Includi logo nell'e-mail" })}
                        />
                        <TextField multiline minRows={3} label={t("Messaggioemail")} value={form.emailMessage} onChange={(e) => updateFormField("emailMessage", e.target.value)} />
                        <Box>
                          <Typography fontWeight={800} sx={{ mb: 1 }}>
                            {t("congressReservations.emailPreview")}
                          </Typography>
                          <Box
                            component="iframe"
                            title={t("congressReservations.emailPreview")}
                            srcDoc={practiceEmailPreviewHtml}
                            sx={{
                              width: "100%",
                              height: { xs: 620, md: 760 },
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              bgcolor: "#f0f0f0",
                            }}
                          />
                        </Box>
                        <Stack spacing={1}>
                          <Typography fontWeight={800}>{t("Allegatiextra")}</Typography>
                          <Button variant="outlined" component="label">
                            {t("Caricaallegati")}
                            <input
                              hidden
                              multiple
                              type="file"
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                updateFormField("newAttachments", [
                                  ...form.newAttachments,
                                  ...files.map((file) => ({ file, description: file.name })),
                                ]);
                                e.target.value = "";
                              }}
                            />
                          </Button>
                          {(form.attachments || []).map((item) => (
                            <Chip key={item.id || item.filePath} label={t("congressReservations.savedAttachment", { name: item.description || item.originalName || t("congressReservations.attachment") })} />
                          ))}
                          {(form.newAttachments || []).map((item, attachmentIndex) => (
                            <Stack key={`${item.file.name}-${attachmentIndex}`} direction="row" spacing={1} alignItems="center">
                              <TextField
                                fullWidth
                                label={`Descrizione allegato: ${item.file.name}`}
                                value={item.description}
                                onChange={(e) => updateFormField("newAttachments", form.newAttachments.map((att, idx) => idx === attachmentIndex ? { ...att, description: e.target.value } : att))}
                              />
                              <Tooltip title={t("elimina")}>
                                <IconButton
                                  color="error"
                                  aria-label={t("elimina")}
                                  onClick={() => updateFormField(
                                    "newAttachments",
                                    form.newAttachments.filter((_, idx) => idx !== attachmentIndex),
                                  )}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          ))}
                        </Stack>
                      </>
                    )}
                  </Stack>
                </Paper>
              )}

              <TextField multiline minRows={3} label={t("Noteorganizzative")} value={form.note} onChange={(e) => updateFormField("note", e.target.value)} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={savingPractice} onClick={() => setDialogOpen(false)}>{t("annulla")}</Button>
            <Button variant="contained" disabled={loading} onClick={handleSave}>
              {t("salvapratica")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(prepaymentDialogEvent)} onClose={closePrepaymentDialog} fullWidth maxWidth="md">
          <DialogTitle>
            {t("congressReservations.prepayments.title", { name: prepaymentDialogEvent?.eventName || "-" })}
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">{t("congressReservations.prepayments.eventTotal")}</Typography>
                    <Typography fontWeight={900}>{formatMoney(prepaymentDialogTotals.practiceTotal)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">{t("congressReservations.prepayments.totalDeposits")}</Typography>
                    <Typography fontWeight={900}>{formatMoney(prepaymentDialogTotals.totalDeposits)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">{t("congressReservations.prepayments.totalAdvances")}</Typography>
                    <Typography fontWeight={900}>{formatMoney(prepaymentDialogTotals.totalAdvances)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">{t("congressReservations.prepayments.amountDue")}</Typography>
                    <Typography fontWeight={900}>{formatMoney(prepaymentDialogTotals.amountDue)}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {loadingPrepaymentSummary ? (
                <Alert severity="info" icon={<CircularProgress size={18} />}>
                  {t("congressReservations.prepayments.loadingSummary")}
                </Alert>
              ) : null}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  variant={prepaymentOperation === "RECEIVE" ? "contained" : "outlined"}
                  onClick={() => changePrepaymentOperation("RECEIVE")}
                >
                  {t("congressReservations.prepayments.receive")}
                </Button>
                <Button
                  variant={prepaymentOperation === "REFUND" ? "contained" : "outlined"}
                  onClick={() => changePrepaymentOperation("REFUND")}
                  disabled={refundablePrepaymentSources.length === 0}
                >
                  {t("congressReservations.prepayments.refund")}
                </Button>
              </Stack>

              {prepaymentOperation === "RECEIVE" ? (
                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <Button
                      variant={prepaymentForm.type === "DEPOSIT" ? "contained" : "outlined"}
                      onClick={() => changePrepaymentType("DEPOSIT")}
                    >
                      {t("congressReservations.prepayments.deposit")}
                    </Button>
                    <Button
                      variant={prepaymentForm.type === "ADVANCE" ? "contained" : "outlined"}
                      onClick={() => changePrepaymentType("ADVANCE")}
                    >
                      {t("congressReservations.prepayments.fiscalAdvance")}
                    </Button>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        required
                        type="number"
                        label={t("congressReservations.prepayments.amount")}
                        value={prepaymentForm.amount}
                        onChange={(e) => updatePrepaymentForm("amount", e.target.value)}
                        inputProps={{ min: 0, step: "0.01" }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        label={t("congressReservations.prepayments.paymentMethod")}
                        value={prepaymentForm.paymentMethod}
                        onChange={(e) => updatePrepaymentForm("paymentMethod", e.target.value)}
                      >
                        {PREPAYMENT_PAYMENT_METHODS.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {t(`congressReservations.prepayments.paymentMethods.${item.labelKey}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {prepaymentForm.type === "ADVANCE" && (
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          required
                          type="number"
                          label={t("congressReservations.prepayments.vatRate")}
                          value={prepaymentForm.vatRate}
                          onChange={(e) => updatePrepaymentForm("vatRate", e.target.value)}
                          inputProps={{ min: 0, max: 100, step: "0.01" }}
                        />
                      </Grid>
                    )}
                    {prepaymentForm.type === "ADVANCE" && (
                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          fullWidth
                          label={t("congressReservations.prepayments.document")}
                          value={prepaymentForm.documentType}
                          onChange={(e) => updatePrepaymentForm("documentType", e.target.value)}
                        >
                          {PREPAYMENT_ADVANCE_DOCUMENT_TYPES.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {t(`congressReservations.prepayments.documentTypes.${item.labelKey}`)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                    {requiresPrepaymentVatNature && (
                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          fullWidth
                          required
                          label={t("congressReservations.prepayments.vatNature")}
                          value={prepaymentForm.vatNature}
                          onChange={(e) => updatePrepaymentForm("vatNature", e.target.value)}
                        >
                          {PREPAYMENT_VAT_NATURE_OPTIONS.map((value) => (
                            <MenuItem key={value} value={value}>
                              {t(`congressReservations.prepayments.vatNatureOptions.${value}`)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                    {requiredPrepaymentDocumentFields && (
                      <>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            required
                            label={t("congressReservations.prepayments.documentNumber")}
                            value={prepaymentForm.documentNumber}
                            onChange={(e) => updatePrepaymentForm("documentNumber", e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            required
                            type="date"
                            label={t("congressReservations.prepayments.documentDate")}
                            value={prepaymentForm.documentDate}
                            onChange={(e) => updatePrepaymentForm("documentDate", e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </>
                    )}
                    {prepaymentForm.type === "ADVANCE" && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          required
                          label={t("congressReservations.prepayments.description")}
                          value={prepaymentForm.description}
                          onChange={(e) => updatePrepaymentForm("description", e.target.value)}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={t("congressReservations.prepayments.notes")}
                        value={prepaymentForm.note}
                        onChange={(e) => updatePrepaymentForm("note", e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  {refundablePrepaymentSources.length === 0 ? (
                    <Alert severity="warning">
                      {t("congressReservations.prepayments.noRefundable")}
                    </Alert>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          select
                          fullWidth
                          required
                          label={t("congressReservations.prepayments.sourceMovement")}
                          value={prepaymentForm.sourceMovementId}
                          onChange={(e) => {
                            const movement = refundablePrepaymentSources.find((item) => Number(item.id) === Number(e.target.value));
                            setPrepaymentForm((prev) => ({
                              ...prev,
                              sourceMovementId: e.target.value,
                              amount: movement?.documentType === "PLAIN_RECEIPT" ? prev.amount : "",
                              documentType: movement?.documentType === "COMMERCIAL_DOCUMENT"
                                ? "COMMERCIAL_RETURN"
                                : movement?.documentType === "INVOICE_TD02"
                                  ? "CREDIT_NOTE_TD04"
                                  : "PLAIN_RECEIPT",
                              invoiceId: "",
                              vatNature: "",
                              documentNumber: "",
                              documentDate: toLocalDateInput(new Date()),
                            }));
                          }}
                        >
                          {refundablePrepaymentSources.map((movement) => (
                            <MenuItem key={movement.id} value={movement.id}>
                              {t("congressReservations.prepayments.movementOptionAvailable", {
                                id: movement.id,
                                type: movement.type === "ADVANCE"
                                  ? t("congressReservations.prepayments.fiscalAdvance")
                                  : t("congressReservations.prepayments.deposit"),
                                amount: formatMoney(movement.amount),
                                available: formatMoney(prepaymentMovementAvailableAmount(movement, prepaymentDialogMovements)),
                                documentType: prepaymentDocumentTypeLabel(movement.documentType, t),
                              })}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      {selectedRefundSource && (
                        <Grid item xs={12}>
                          <Alert severity="info">
                            {t("congressReservations.prepayments.originalDocument", {
                              documentType: prepaymentDocumentTypeLabel(selectedRefundSource.documentType, t),
                              documentNumber: selectedRefundSource.documentNumber || "",
                            })}
                          </Alert>
                        </Grid>
                      )}
                      {selectedRefundIsPlainReceipt && (
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            required
                            type="number"
                            label={t("congressReservations.prepayments.refundAmount")}
                            value={prepaymentForm.amount}
                            onChange={(e) => updatePrepaymentForm("amount", e.target.value)}
                            inputProps={{ min: 0, max: selectedRefundAvailableAmount || undefined, step: "0.01" }}
                          />
                        </Grid>
                      )}
                      {selectedRefundIsReverse && (
                        <Grid item xs={12}>
                          <Alert severity="warning">
                            {t("congressReservations.prepayments.fullReversalInfo", {
                              amount: formatMoney(selectedRefundAvailableAmount || selectedRefundSource.amount),
                            })}
                          </Alert>
                        </Grid>
                      )}
                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          fullWidth
                          label={t("congressReservations.prepayments.paymentMethod")}
                          value={prepaymentForm.paymentMethod}
                          onChange={(e) => updatePrepaymentForm("paymentMethod", e.target.value)}
                        >
                          {PREPAYMENT_PAYMENT_METHODS.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {t(`congressReservations.prepayments.paymentMethods.${item.labelKey}`)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          fullWidth
                          label={t("congressReservations.prepayments.refundDocument")}
                          value={prepaymentForm.documentType}
                          onChange={(e) => updatePrepaymentForm("documentType", e.target.value)}
                        >
                          {prepaymentRefundDocumentOptions.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {t(`congressReservations.prepayments.documentTypes.${item.labelKey}`)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      {requiredPrepaymentDocumentFields && (
                        <>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              required
                              label={t("congressReservations.prepayments.documentNumber")}
                              value={prepaymentForm.documentNumber}
                              onChange={(e) => updatePrepaymentForm("documentNumber", e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              required
                              type="date"
                              label={t("congressReservations.prepayments.documentDate")}
                              value={prepaymentForm.documentDate}
                              onChange={(e) => updatePrepaymentForm("documentDate", e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label={t("congressReservations.prepayments.notes")}
                          value={prepaymentForm.note}
                          onChange={(e) => updatePrepaymentForm("note", e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closePrepaymentDialog} disabled={savingPrepayment}>{t("annulla")}</Button>
            <Button
              variant="contained"
              disabled={savingPrepayment || (prepaymentOperation === "REFUND" && refundablePrepaymentSources.length === 0)}
              onClick={submitPrepayment}
            >
              {savingPrepayment
                ? t("congressReservations.prepayments.saving")
                : t("congressReservations.prepayments.register")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(setupManagerTable)} onClose={() => setSetupManagerTable(null)} fullWidth maxWidth="sm">
          <DialogTitle>{t("Allestimenticompatibili")} - {setupManagerTable?.name}</DialogTitle>
          <DialogContent dividers>
            <Autocomplete
              multiple
              options={setups.filter((setup) => setup.active !== false)}
              value={setups.filter((setup) => setupManagerSetupIds.includes(setup.id))}
              onChange={(_, value) => setSetupManagerSetupIds(value.map((item) => item.id))}
              getOptionLabel={(option) => option?.name || ""}
              renderOption={(props, option) => {
                const preview = Array.isArray(option.imagePaths) ? option.imagePaths.find(Boolean) : null;
                return (
                  <Box component="li" {...props} sx={{ gap: 1.5, alignItems: "center" }}>
                    {preview ? (
                      <Box
                        component="img"
                        src={setupImageSrc(preview)}
                        alt={option.name || t("setups.imageAlt")}
                        sx={{ width: 64, height: 48, borderRadius: 1, objectFit: "cover", flexShrink: 0 }}
                      />
                    ) : (
                      <Box sx={{ width: 64, height: 48, borderRadius: 1, bgcolor: "action.hover", flexShrink: 0 }} />
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={700} noWrap>{option.name}</Typography>
                      {option.description && <Typography variant="caption" color="text.secondary" noWrap>{option.description}</Typography>}
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => <TextField {...params} label={t("Allestimenticompatibili")} />}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t("durante")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSetupManagerTable(null)}>{t("annulla")}</Button>
            <Button variant="contained" onClick={saveSetupManager}>
              {t("salvacompatibilita")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={summaryEmailOpen} onClose={() => setSummaryEmailOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>{t("InviaRiepilogoFiltrato")}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  autoFocus
                  fullWidth
                  type="email"
                  label={t("congressReservations.emailAddresses")}
                  value={summaryRecipientDraft}
                  onChange={(e) => setSummaryRecipientDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSummaryRecipient();
                    }
                  }}
                />
                <Tooltip title={t("aggiungi")}>
                  <IconButton
                    color="primary"
                    aria-label={t("aggiungi")}
                    onClick={addSummaryRecipient}
                    sx={{ mt: 0.5 }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              {summaryRecipients.map((email) => (
                <Stack key={email} direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ flex: 1, overflowWrap: "anywhere" }}>{email}</Typography>
                  <Tooltip title={t("elimina")}>
                    <IconButton
                      color="error"
                      aria-label={t("elimina")}
                      onClick={() => setSummaryRecipients((prev) => prev.filter((item) => item !== email))}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
              <Button
                variant="outlined"
                onClick={() => {
                  setSummaryRecipients([]);
                  setSummaryRecipientDraft("");
                }}
                disabled={summaryRecipients.length === 0 && !summaryRecipientDraft}
              >
                {t("pulisci")}
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSummaryEmailOpen(false)}>{t("annulla")}</Button>
            <Button variant="contained" onClick={handleSendSummaryEmail}>
              {t("inviaPDF")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(practiceConfirmDialog)}
          onClose={() => closePracticeConfirmDialog(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>{practiceConfirmDialog?.title}</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2, whiteSpace: "pre-line" }}>
              {practiceConfirmDialog?.message}
            </Alert>
            <DialogContentText>
              {practiceConfirmDialog?.helpText || t("congressReservations.confirmActionHelp")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => closePracticeConfirmDialog(false)}>
              {practiceConfirmDialog?.cancelLabel || t("annulla")}
            </Button>
            <Button
              variant="contained"
              color={practiceConfirmDialog?.confirmColor || "warning"}
              onClick={() => closePracticeConfirmDialog(true)}
            >
              {practiceConfirmDialog?.confirmLabel || t("conferma")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={cancelReasonOpen} onClose={() => setCancelReasonOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t("Annullapratica")}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t("Perannullareunapratica")}
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              label={t("congressReservations.cancellationReason")}
              placeholder={t("congressReservations.cancellationReasonPlaceholder")}
              value={form.cancellationReason}
              onChange={(e) => {
                updateFormField("cancellationReason", e.target.value);
                if (cancelReasonError) setCancelReasonError("");
              }}
              error={Boolean(cancelReasonError)}
              helperText={cancelReasonError}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelReasonOpen(false)}>{t("annulla")}</Button>
            <Button variant="contained" color="error" disabled={loading} onClick={submitCancellationReason}>
              {t("confermaanullamento")}
            </Button>
          </DialogActions>
        </Dialog>

        <Portal>
          <Backdrop
            open={savingPractice}
            sx={{
              color: "#fff",
              bgcolor: "rgba(0, 0, 0, 0.78)",
              zIndex: (muiTheme) => muiTheme.zIndex.modal + 100,
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress color="inherit" />
            <Typography variant="h6" fontWeight={700}>
              {t("congressReservations.savingPractice")}
            </Typography>
          </Backdrop>
        </Portal>
      </Box>
    </SidebarLayout>
  );
}
